import React, { useState, useEffect, useRef, useMemo }from "react";
import { useNavigate }from "react-router-dom";
import { motion, AnimatePresence }from "framer-motion";
import { 
  // Core icons - use unique names for each
  RocketIcon, 
  CheckCircle2, 
  ArrowRight,
  Building2, 
  Users2, 
  BarChart3, 
  Target, 
  Lightbulb, 
  Globe,
  ArrowUpRight,
  ChevronRight,
  BarChart4,
  AlertTriangle,
  Cpu,
  Search,
  Orbit,
  Wifi,
  Laptop,
  Database,
  LineChart,
  Users,
  Zap,
  Network,
  BarChart, 
  PieChart, 
  TrendingUp, 
  Award, 
  Megaphone, 
  MessagesSquare, 
  Layers, 
  ArrowUpCircle,
  Star,
  Link,
  PenTool,
  LayoutGrid,
  File,
  Share2,
  Compass,
  Settings,
  Rocket, 
  CheckCircle,
  MoveRight,
  Shield,
  Building,
  Trophy,
  DollarSign,
  MapPin,
  Check,
  BadgeCheck,
  FileText,
  Bookmark,
  Flag,
  MousePointer,
  CircleDot,
  Swords,
  Leaf,
  Heart,
  AlertCircle,
  Box,
  Fingerprint,
  Tag,
  UsersRound,
  BarChart2,
  ExternalLink,
  Home,
  ChevronLeft,
  X,
  ChevronDown,
  Info,
  ChevronUp,
  MailOpen,
  PenLine,
  Wrench,
  FileWarning,
  Download,
  RefreshCw,
  Loader2,
  Package,
  FileKey,
  FileQuestion,
  Hash,
  FileX,
  Loader,
  MoreHorizontal,
  Clock,
  MessageSquare,
  Video,
  Camera,
  MessageCircle,
  UserPlus,
  Handshake,
  CalendarDays,
  CircleX,
  Mic,
  Mail,
  BookOpen,
  MonitorPlay
}from "lucide-react";
// Create separate imports for duplicates to avoid conflicts
import { Globe as GlobeIcon }from "lucide-react";
import { Users as UsersIcon }from "lucide-react";
import { Rocket as RocketIcon2 }from "lucide-react";

import { Button }from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle }from "@/components/ui/card";
import { useForm }from "react-hook-form";
import { z }from "zod";
import { zodResolver }from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
}from "@/components/ui/form";
import { Input }from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
}from "@/components/ui/select";
import { Textarea }from "@/components/ui/textarea";
import { Progress }from "@/components/ui/progress";
import { Confetti }from "@/components/ui/confetti";
import { cn }from "@/lib/utils";
import { useToast }from "@/hooks/use-toast";
import { AnalyzeStep }from "@/components/workspace/AnalyzeStep";
import { supabase }from "@/integrations/supabase/client";
import { ResearchInsights }from "./ResearchInsights";
import { Alert, AlertDescription }from "@/components/ui/alert";
import { AITeamLoading }from "@/components/workspace/AITeamLoading";
import { Tabs, TabsContent, TabsList, TabsTrigger }from "@/components/ui/tabs";
import { Avatar, AvatarFallback }from "@/components/ui/avatar";
import { Badge }from "@/components/ui/badge";
import { getUserCurrency } from "@/utils/currency";

const companyTypes = [
  { id: "b2b", name: "B2B" },
  { id: "b2c", name: "B2C" },
];

const industries = [
  "Technology",
  "Healthcare",
  "Finance",
  "Education",
  "E-commerce",
  "Marketing",
  "Real Estate",
  "Manufacturing",
  "Transportation",
  "Entertainment",
  "Energy",
  "Agriculture",
  "Personal Branding",
  "Other",
];

const projectSchema = z.object({
  businessName: z.string().min(1, "Business name is required"),
  companyType: z.string().min(1, "Company type is required"),
  industry: z.string().min(1, "Industry is required"),
  xHandle: z.string().optional(),
});

type ProjectFormValues = z.infer<typeof projectSchema>;

interface OnboardingWizardProps {
  onComplete: (values: ProjectFormValues) => void;
  isLoading: boolean;
  onStepChange?: (step: number) => void;
}

interface CommitmentAnswers {
  // Market Deep-Dive
  seriousnessScale: string;
  biggestChallenge: string;
  revenueGoal: string;
  timeCommitment: string;
  
  // Audience Insights
  idealCustomer: string;
  mainProblem: string;
  customerHangout: string;
  marketingChannels: string;
  
  // Content Strategy
  brandTopics: string;
  discoveryMethod: string;
  contentFormats: string;
  targetKeywords: string;
}

// Component for hero particles
const HeroParticle = ({ delay = 0, x = 0, y = 0, size = 6 }) => (
  <motion.div
    className="absolute rounded-full bg-blue-500 opacity-70"
    style={{ 
      width: size, 
      height: size, 
      left: `calc(50% + ${x}px)`, 
      top: `calc(50% + ${y}px)` 
    }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{ 
      opacity: [0, 0.8, 0],
      scale: [0, 1, 0],
      x: [0, x*2, x*3],
      y: [0, y*2, y*3],
    }}
    transition={{ 
      duration: 3,
      times: [0, 0.5, 1],
      delay,
      repeat: Infinity, 
      repeatType: "loop",
      repeatDelay: Math.random() * 2
    }}
  />
);

// Mouse-reactive background component with performance optimization
const MouseReactiveBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  // Generate static random values for particles to prevent re-renders
  const particles = useMemo(() => 
    Array.from({ length: 8 }).map(() => ({
      size: Math.random() * 8 + 4,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: Math.random() * 5,
      duration: Math.random() * 5 + 5,
      xFactor: Math.random() * -30 - 10,
      yFactor: Math.random() * -30 - 10,
    })), 
  []);
  
  // Handle mouse movement with throttling for performance
  useEffect(() => {
    // Detect touch device
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (timeoutId) return;
      
      timeoutId = setTimeout(() => {
        // Get viewport dimensions for relative positioning
        const { innerWidth, innerHeight }= window;
        // Calculate mouse position as percentage of viewport with bounds checking
        const x = Math.max(-0.5, Math.min(0.5, (e.clientX / innerWidth) - 0.5));
        const y = Math.max(-0.5, Math.min(0.5, (e.clientY / innerHeight) - 0.5));
        
        setMousePosition({ x, y });
        timeoutId = null;
      }, 50); // Throttle to 50ms for better performance
    };
    
    if (!isTouchDevice) {
      window.addEventListener('mousemove', handleMouseMove);
    }else {
      // Add subtle auto-animation for touch devices
      const interval = setInterval(() => {
        setMousePosition(prev => ({
          x: Math.sin(Date.now() / 5000) * 0.1,
          y: Math.cos(Date.now() / 5000) * 0.1
        }));
      }, 100);
      
      return () => clearInterval(interval);
    }
    
    return () => {
      if (!isTouchDevice) {
        window.removeEventListener('mousemove', handleMouseMove);
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isTouchDevice]);
  
  return (
    <div className="absolute inset-0 overflow-hidden -z-10 pointer-events-none">
      <motion.div 
        className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50/30 via-blue-50/40 to-blue-50/30 dark:from-blue-900/10 dark:via-blue-900/10 dark:to-blue-900/10"
        animate={{
          x: mousePosition.x * -15,
          y: mousePosition.y * -15
        }}
        transition={{ 
          type: "spring", 
          stiffness: 40, 
          damping: 30,
          mass: 2 // Heavier mass for smoother movement
        }}
      />
      
      {/* Reduced number of particles for better performance */}
      {particles.map((particle, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute rounded-full bg-blue-400/10 dark:bg-blue-400/5"
          style={{
            width: particle.size,
            height: particle.size,
            left: particle.left,
            top: particle.top,
          }}
          animate={{
            opacity: [0.4, 0.8, 0.4],
            x: mousePosition.x * particle.xFactor,
            y: [0, -15, 0].map(v => v + (mousePosition.y * particle.yFactor)),
          }}
          transition={{
            opacity: {
              duration: particle.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: particle.delay,
            },
            x: { 
              type: "spring", 
              stiffness: 20, 
              damping: 25 
            },
            y: { 
              duration: particle.duration,
              repeat: Infinity, 
              ease: "easeInOut"
            }
          }}
        />
      ))}
      
      {/* Decorative lines - now with pre-rendered SVGs for better performance */}
      <motion.div 
        className="absolute top-10 left-0 w-24 h-24 text-blue-500/10 dark:text-blue-400/5"
        animate={{
          x: mousePosition.x * -30,
          y: mousePosition.y * -30,
          rotate: mousePosition.x * 3,
        }}
        transition={{ 
          type: "spring", 
          stiffness: 40, 
          damping: 30,
          mass: 1.5
        }}
      >
        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <motion.path 
            d="M10 50 L90 50" 
            stroke="currentColor" 
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, repeat: Infinity, repeatType: "loop", repeatDelay: 2 }}
          />
          <motion.path 
            d="M10 70 L70 70" 
            stroke="currentColor" 
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, repeat: Infinity, repeatType: "loop", repeatDelay: 2, delay: 0.5 }}
          />
          <motion.path 
            d="M10 90 L50 90" 
            stroke="currentColor" 
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, repeat: Infinity, repeatType: "loop", repeatDelay: 2, delay: 1 }}
          />
        </svg>
      </motion.div>

      <motion.div 
        className="absolute bottom-10 right-0 w-32 h-32 text-blue-500/10 dark:text-blue-400/5"
        animate={{
          x: mousePosition.x * 30,
          y: mousePosition.y * 30,
          rotate: mousePosition.y * -3,
        }}
        transition={{ 
          type: "spring", 
          stiffness: 40, 
          damping: 30,
          mass: 1.5
        }}
      >
        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <motion.circle 
            cx="50" 
            cy="50" 
            r="40" 
            stroke="currentColor" 
            strokeWidth="2"
            initial={{ pathLength: 0, rotate: 0 }}
            animate={{ pathLength: 1, rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, repeatType: "loop", ease: "linear" }}
          />
        </svg>
      </motion.div>
    </div>
  );
};

// Intro Animation Component
const IntroAnimation = ({ onComplete }: { onComplete: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3200);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div 
      className="fixed inset-0 bg-gradient-to-b from-primary/90 via-primary to-primary/95 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
    >
      <div className="relative w-full max-w-md flex items-center justify-center px-6">
        {Array.from({ length: 16 }).map((_, i) => (
          <HeroParticle 
            key={i}
            delay={i * 0.08}
            x={Math.random() * 160 - 80}
            y={Math.random() * 160 - 80}
            size={Math.random() * 6 + 2}
          />
        ))}
        
        <motion.div 
          className="relative z-10 flex flex-col items-center text-center"
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div 
            className="size-20 md:size-24 rounded-2xl bg-primary-foreground/10 flex items-center justify-center mb-5 shadow-2xl ring-1 ring-primary-foreground/20"
            initial={{ y: 12 }}
            animate={{ y: 0 }}
            transition={{ type: "spring", stiffness: 180, damping: 12, delay: 0.3 }}
          >
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 rounded-2xl border-2 border-primary-foreground/20 border-t-primary-foreground/50"
            />
            <RocketIcon className="size-10 md:size-12 text-primary-foreground relative z-10" />
          </motion.div>
          
          <motion.h1 
            className="text-3xl md:text-4xl font-bold text-primary-foreground mb-2 tracking-tight"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            LaunchGuide
          </motion.h1>
          
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "60%" }}
            transition={{ duration: 1.2, delay: 0.9, ease: "easeOut" }}
            className="h-1 bg-primary-foreground/40 rounded-full mb-4"
          />
          
          <motion.p 
            className="text-primary-foreground/80 text-sm md:text-base max-w-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 1.2 }}
          >
            Your roadmap to launch and growth
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Results celebration component that appears after analysis completes
const ResultsCelebration = ({ onComplete }: { onComplete: () => void }) => {
  // Auto-complete after timer
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [onComplete]);
  
  return (
    <div className="flex flex-col items-center justify-center text-center p-8">
      <h3 className="text-xl font-semibold mb-2 text-foreground">
        Analysis complete
      </h3>
      <p className="text-muted-foreground mb-4">
        Preparing your personalized insights...
      </p>
    </div>
  );
};

// Globe Visualization Component
const GlobeVisualization = ({ scanning, progress }: { scanning: boolean, progress: number }) => {
  const orbitParticles = useMemo(() => 
    Array.from({ length: 12 }).map(() => ({
      rotation: Math.random() * 360,
      speed: Math.random() * 15 + 5,
      size: Math.random() * 3 + 1.5,
      opacity: Math.random() * 0.5 + 0.3,
      offset: Math.random() * 10,
    })), 
  []);

  return (
    <div className="relative w-32 h-32 mx-auto mb-8">
      {/* Glowing background for the globe */}
      <motion.div
        className="absolute inset-0 rounded-full bg-blue-500/10 dark:bg-blue-500/20 blur-xl"
        animate={scanning ? { 
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5]
        }: { }}
        transition={{ 
          duration: 3,
          repeat: scanning ? Infinity : 0,
          repeatType: "reverse" 
        }}
      />
      
      {/* Scanning waves emanating from the globe */}
      {scanning && (
        <>
          {[0, 1, 2, 3].map((idx) => (
            <motion.div
              key={`wave-${idx}`}
              className="absolute inset-0 rounded-full border border-blue-400/30 dark:border-blue-400/20"
              initial={{ scale: 1, opacity: 0.8 }}
              animate={{ scale: 1.8, opacity: 0 }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: idx * 0.5,
                ease: "easeOut"
              }}
            />
          ))}
        </>
      )}
      
      {/* Orbital rings */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[1, 2, 3].map((orbitIdx) => (
          <motion.div
            key={`orbit-${orbitIdx}`}
            className={cn(
              "absolute rounded-full border opacity-20",
              orbitIdx === 1 ? "border-blue-400 dark:border-blue-300" : 
              orbitIdx === 2 ? "border-blue-400 dark:border-blue-300" : 
              "border-blue-400 dark:border-blue-300"
            )}
            style={{
              width: `${70 + (orbitIdx * 15)}%`,
              height: `${70 + (orbitIdx * 15)}%`,
              transform: `rotate(${orbitIdx * 30}deg)`
            }}
            animate={{ rotate: 360 }}
            transition={{
              duration: 20 + (orbitIdx * 5),
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>
      
      {/* Orbital particles */}
      {orbitParticles.map((particle, idx) => (
        <motion.div
          key={`particle-${idx}`}
          className="absolute rounded-full bg-blue-400 dark:bg-blue-300"
          style={{
            width: particle.size,
            height: particle.size,
            top: '50%',
            left: '50%',
            opacity: particle.opacity,
          }}
          initial={{
            rotate: particle.rotation
          }}
          animate={{
            rotate: particle.rotation + 360
          }}
          transition={{
            duration: particle.speed,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <motion.div
            className="absolute bg-blue-400 dark:bg-blue-300 rounded-full"
            style={{
              width: particle.size,
              height: particle.size,
              left: `${50 + particle.offset}%`,
              top: 0,
              transform: 'translate(-50%, -50%)'
            }}
          />
        </motion.div>
      ))}
      
      {/* The globe itself */}
      <motion.div 
        className={cn(
          "absolute inset-0 rounded-full bg-gradient-to-b from-blue-100 via-blue-50 to-blue-100 dark:from-blue-900 dark:via-blue-800 dark:to-blue-900 flex items-center justify-center z-10",
          scanning ? "shadow-lg shadow-blue-500/20" : ""
        )}
        animate={scanning ? {
          scale: [1, 1.03, 1],
        }: { }}
        transition={{
          duration: 2,
          repeat: scanning ? Infinity : 0,
          repeatType: "reverse"
        }}
      >
        {/* Globe icon and effects */}
        <div className="relative">
          <Globe 
            className={cn(
              "w-12 h-12 text-blue-500 dark:text-blue-400",
              scanning ? "opacity-80" : "opacity-100"
            )}
          />
          
          {/* Scanning effect */}
          {scanning && (
            <motion.div 
              className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/20 to-transparent"
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 30, opacity: [0, 1, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          )}
        </div>
      </motion.div>
    
      {/* Progress indicator */}
      {scanning && progress > 0 && (
        <div className="absolute -bottom-6 left-0 right-0 flex justify-center">
          <div className="bg-zinc-100 dark:bg-zinc-800 rounded-full h-1.5 w-20 overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-500 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced URL Input Component
const EnhancedUrlInput = ({ 
  value, 
  onChange, 
  onSubmit, 
  isLoading,
  isValid = false
}: { 
  value: string, 
  onChange: (value: string) => void, 
  onSubmit: () => void, 
  isLoading: boolean,
  isValid?: boolean
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Placeholder animation logic
  const [placeholder, setPlaceholder] = useState('https://yourwebsite.com');
  
  useEffect(() => {
    if (!value && !isFocused) {
      const examples = [
        'https://yourwebsite.com',
        'https://example.com',
        'https://mybusiness.com',
        'https://yourcompany.co',
      ];
      
      let index = 0;
      
      const interval = setInterval(() => {
        // First fade out current placeholder
        if (inputRef.current) {
          inputRef.current.style.opacity = '0';
        }
        
        // Then change text and fade back in
        setTimeout(() => {
          index = (index + 1) % examples.length;
          setPlaceholder(examples[index]);
          if (inputRef.current) {
            inputRef.current.style.opacity = '0.6';
          }
        }, 300);
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [value, isFocused]);
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && value.trim()) {
      onSubmit();
    }
  };
  
  // Domain extraction for favicon
  const domain = useMemo(() => {
    try {
      if (value && value.includes('.')) {
        const url = value.startsWith('http') ? value : `https://${value}`;
        return new URL(url).hostname;
      }
      return '';
    }catch {
      return '';
    }
  }, [value]);
  
  return (
    <div className="relative w-full max-w-md mx-auto mb-6">
      <div 
        className={cn(
          "relative group transition-all duration-300 rounded-xl overflow-hidden",
          isFocused ? "shadow-lg ring-2 ring-blue-500/50" : "shadow",
        )}
      >
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 rounded-xl opacity-0 group-hover:opacity-30 group-focus-within:opacity-30 blur-md transition-all duration-300" />
        
        <div className="relative flex items-center bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 transition-all duration-300">
          <Search className="absolute left-3 w-5 h-5 text-zinc-400" />
          
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            disabled={isLoading}
            className={cn(
              "flex-1 h-14 px-10 bg-transparent border-0 focus:ring-0 transition-opacity duration-300",
              isLoading ? "opacity-70 cursor-not-allowed" : ""
            )}
          />
          
          {domain && !isLoading && (
            <div className="absolute right-14 flex items-center">
              <div className="rounded bg-zinc-100 dark:bg-zinc-700 p-1.5">
                <Wifi className="w-4 h-4 text-green-500" />
              </div>
            </div>
          )}
          
          <Button 
            type="button"
            size="sm"
            disabled={isLoading || !value.trim()}
            onClick={onSubmit}
            className={cn(
              "absolute right-2 transition-all",
              isValid || domain ? "bg-blue-500 hover:bg-blue-600" : "",
              !value.trim() ? "opacity-70" : ""
            )}
          >
            {isLoading ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <Orbit className="w-4 h-4" />
              </motion.div>
            ) : "Analyze"}
          </Button>
        </div>
      </div>
      
      {domain && !isLoading && (
        <motion.div 
          className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 pl-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="w-3 h-3 rounded-full bg-green-500" />
          Domain ready for analysis
        </motion.div>
      )}
    </div>
  );
};

// AI Team Visualization Component
const AITeamAnalysis = ({ 
  progress = 0, 
  currentTask = '',
  isComplete = false 
}: { 
  progress?: number, 
  currentTask?: string,
  isComplete?: boolean
}) => {
  // Define analysis phases with icons and descriptions
  const phases = [
    { 
      id: 'crawling', 
      name: 'Web Crawler', 
      description: 'Scanning website content and structure',
      icon: Laptop,
      color: 'text-blue-500 dark:text-blue-400'
    },
    {
      id: 'data',
      name: 'Data Processor',
      description: 'Processing website information',
      icon: Database,
      color: 'text-blue-500 dark:text-blue-400'
    },
    { 
      id: 'seo', 
      name: 'SEO Analyzer', 
      description: 'Evaluating search engine factors',
      icon: Search,
      color: 'text-violet-500 dark:text-violet-400'
    },
    { 
      id: 'audience', 
      name: 'Audience Profiler', 
      description: 'Identifying target customer segments',
      icon: Users,
      color: 'text-emerald-500 dark:text-emerald-400'
    },
    { 
      id: 'insights', 
      name: 'Insight Generator', 
      description: 'Creating personalized recommendations',
      icon: Zap,
      color: 'text-amber-500 dark:text-amber-400'
    },
  ];
  
  // Calculate current phase based on progress
  const currentPhaseIndex = isComplete 
    ? phases.length - 1 
    : Math.min(Math.floor(progress / (100 / phases.length)), phases.length - 1);
  
  // Random data for the connection lines
  const connectionData = useMemo(() => 
    phases.map((_, idx) => ({
      pathVariation: idx % 2 === 0 
        ? 'M 0,0 C 50,-20 50,20 100,0' 
        : 'M 0,0 C 50,20 50,-20 100,0',
      pulseDelay: idx * 1,
      pulseSize: Math.random() * 2 + 3,
      pulseSpeed: Math.random() * 2 + 2
    })),
  [phases]);
  
  // Messages for each phase
  const phaseMessages = {
    crawling: [
      "Crawling website pages...",
      "Reading content structure...",
      "Analyzing page hierarchy...",
      "Mapping site architecture..."
    ],
    data: [
      "Processing website data...",
      "Extracting key information...",
      "Analyzing content quality...",
      "Evaluating design elements..."
    ],
    seo: [
      "Analyzing keyword optimization...",
      "Checking meta information...",
      "Evaluating content relevance...",
      "Checking performance factors..."
    ],
    audience: [
      "Identifying target segments...",
      "Analyzing user journey...",
      "Mapping customer touchpoints...",
      "Evaluating conversion paths..."
    ],
    insights: [
      "Generating growth insights...",
      "Creating personalized recommendations...",
      "Finalizing opportunity assessment...",
      "Preparing findings report..."
    ]
  };
  
  // Get current task message based on the phase
  const getTaskMessage = (phaseId: string) => {
    const messages = phaseMessages[phaseId as keyof typeof phaseMessages];
    if (!messages) return "";
    
    // Use the provided task message if available, otherwise pick a random one
    return currentTask || messages[Math.floor(Math.random() * messages.length)];
  };
  
  return (
    <div className="relative py-8 px-2">
      {/* Main container */}
      <div className="max-w-xl mx-auto">
        {/* Progress bar at top */}
        <div className="mb-10 px-4">
          <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-500 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          
          {/* Phase steps with connecting lines */}
          <div className="relative mt-6">
            <div className="flex justify-between items-center">
              {phases.map((phase, idx) => {
                const PhaseIcon = phase.icon;
                const isActive = idx <= currentPhaseIndex;
                const isCurrent = idx === currentPhaseIndex;
                
                return (
                  <div key={phase.id}className="relative flex flex-col items-center">
                    {/* Icon circle */}
                    <motion.div 
                      className={cn(
                        "relative z-10 rounded-full flex items-center justify-center transition-all duration-300",
                        isActive 
                          ? `${phase.color}bg-blue-50 dark:bg-zinc-800` 
                          : "text-zinc-400 bg-zinc-100 dark:bg-zinc-800"
                      )}
                      style={{
                        width: isCurrent ? 52 : 44,
                        height: isCurrent ? 52 : 44,
                      }}
                      animate={isCurrent ? {
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          "0 0 0 0 rgba(59, 130, 246, 0)",
                          "0 0 0 8px rgba(59, 130, 246, 0.2)",
                          "0 0 0 0 rgba(59, 130, 246, 0)"
                        ]
                      }: isActive ? {
                        scale: 1
                      }: {
                        scale: 0.9
                      }}
                      transition={{
                        duration: 2,
                        repeat: isCurrent ? Infinity : 0,
                        repeatType: "loop"
                      }}
                    >
                      <PhaseIcon size={isCurrent ? 28 : 24}/>
                      
                      {/* Animated processing effect */}
                      {isCurrent && !isComplete && (
                        <motion.div
                          className="absolute inset-0 rounded-full border-2 border-blue-500"
                          animate={{
                            scale: [1, 1.15, 1],
                            opacity: [1, 0, 1]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "loop"
                          }}
                        />
                      )}
                    </motion.div>
                    
                    {/* Phase name */}
                    <div className="text-center mt-3">
                      <div 
                        className={cn(
                          "text-xs font-medium transition-colors duration-300",
                          isActive 
                            ? "text-zinc-800 dark:text-zinc-100 hover:text-zinc-900 dark:hover:text-white" 
                            : "text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
                        )}
                      >
                        {phase.name}
                      </div>
                    </div>
                    
                    {/* Connection line to next phase */}
                    {idx < phases.length - 1 && (
                      <div 
                        className="absolute top-6 left-full h-0.5 bg-zinc-200 dark:bg-zinc-700 z-0"
                        style={{ width: 'calc(100% - 18px)', transformOrigin: 'left' }}
                      >
                        {isActive && !isComplete && (
                          <svg width="100%" height="10" className="absolute top-1/2 -translate-y-1/2">
                            <motion.circle
                              r={connectionData[idx].pulseSize}
                              fill="#3b82f6"
                              filter="url(#glow)"
                              initial={{ cx: 0 }}
                              animate={{ cx: [0, '100%'] }}
                              transition={{
                                duration: connectionData[idx].pulseSpeed,
                                repeat: Infinity,
                                delay: connectionData[idx].pulseDelay,
                                ease: "easeInOut"
                              }}
                            />
                            <defs>
                              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                              </filter>
                            </defs>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Current analysis message */}
        {!isComplete && currentPhaseIndex >= 0 && (
          <motion.div 
            className="text-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            key={`message-${currentPhaseIndex}`}
          >
            <div className="text-sm text-zinc-500 dark:text-zinc-400">
              {getTaskMessage(phases[currentPhaseIndex].id)}
            </div>
            
            {/* Analysis activity visualization */}
            <div className="mt-3 flex items-center justify-center gap-1">
              {[0, 1, 2].map((dot) => (
                <motion.div 
                  key={`activity-dot-${dot}`}
                  className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: dot * 0.2,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
        
        {/* Completion state */}
        {isComplete && (
          <motion.div 
            className="text-center px-4 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
            >
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </motion.div>
            
            <h3 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-2">
              Analysis Complete!
            </h3>
            
            <p className="text-zinc-600 dark:text-zinc-400 max-w-md mx-auto mb-4">
              We've gathered key insights about your product, audience, and market position. 
              Click continue to view your personalized growth insights.
            </p>
            
            <div className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400 font-medium">
              <span>Click the Continue button below</span>
              <motion.div
                animate={{ y: [0, 3, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight className="h-4 w-4" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Modify EnhancedAnalyzeStep to use the existing AnalyzeStep component properly
const EnhancedAnalyzeStep = ({ 
  onAnalysisComplete, 
  onAnalysisStart,
  projectId
}: { 
  onAnalysisComplete: (results: any) => void; 
  onAnalysisStart: (url?: string) => void;
  projectId: string | null;
}) => {
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [urlValid, setUrlValid] = useState(false);
  const { toast }= useToast();
  
  // State to control when to render the actual analysis component
  const [showAnalyzer, setShowAnalyzer] = useState(false);
  const [urlToAnalyze, setUrlToAnalyze] = useState('');
  
  // Check URL validity
  useEffect(() => {
    try {
      const testUrl = url.includes('://') ? url : `https://${url}`;
      new URL(testUrl);
      setUrlValid(true);
    }catch {
      setUrlValid(false);
    }
  }, [url]);
  
  // Handle starting the analysis from our UI
  const handleStartAnalysis = () => {
    if (!url || isAnalyzing) return;
    
    // Format URL if needed
    const formattedUrl = url.includes('://') ? url : `https://${url}`;
    
    // Store the URL first
    setUrlToAnalyze(formattedUrl);
    
    // Notify parent
    onAnalysisStart(formattedUrl);
    
    // Then set the analyzing state
    setIsAnalyzing(true);
    
    // Finally show the analyzer
    setShowAnalyzer(true);
  };
  
  // This is a handler for the embedded AnalyzeStep component's progress updates
  // We'll manually implement this since AnalyzeStep doesn't expose progress directly
  useEffect(() => {
    if (isAnalyzing) {
      // Use a slower interval to match the expected 60+ second analysis time
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          // Make the increments even smaller for a longer analysis time
          // This will make the analysis take ~75 seconds to reach 95%
          return Math.min(prev + (Math.random() * 0.7) + 0.2, 95);
        });
        
        // Randomly update task messages with more detailed tasks
        const tasks = [
          "Initializing website crawler...",
          "Scanning website structure...",
          "Mapping site architecture...",
          "Extracting content elements...",
          "Analyzing page content quality...",
          "Evaluating SEO factors...",
          "Checking keyword optimization...",
          "Analyzing meta information...",
          "Identifying target audience segments...",
          "Mapping user journey paths...",
          "Analyzing competitor positioning...",
          "Evaluating market differentiation...",
          "Analyzing technical performance...",
          "Generating strategic insights...",
          "Calculating growth opportunities...",
          "Prioritizing recommendations...",
          "Finalizing growth strategy blueprint..."
        ];
        
        // Update task messages more frequently for a more dynamic experience
        if (Math.random() > 0.6) {
          const progressPercent = progress / 100;
          const taskIndex = Math.min(
            Math.floor(progressPercent * tasks.length),
            tasks.length - 1
          );
          setCurrentTask(tasks[taskIndex]);
        }
      }, 400);
      
      return () => clearInterval(progressInterval);
    }
  }, [isAnalyzing, progress]);
  
  // This is a handler for when the analysis completes from AnalyzeStep
  const handleAnalysisCompleteFromStep = (analysisResults: any) => {
    // Check if we have valid results
    if (!analysisResults) {
      setIsAnalyzing(false);
      setProgress(0);
      setCurrentTask("");
      setShowAnalyzer(false); // Make sure to hide the analyzer
      toast({
        title: "Analysis Failed",
        description: "No analysis results were returned. Please try a different URL.",
        variant: "destructive",
      });
      
      // ✅ CRITICAL FIX: Notify parent wizard about analysis failure
      onAnalysisComplete(null);
      return;
    }
    
    // Set progress to 100% and complete state
    setProgress(100);
    setCurrentTask("Analysis complete!");
    setIsComplete(true);
    
    // Short delay to show the completion state before resetting
    // Make this delay longer to ensure the user sees the completed state
    setTimeout(() => {
      setIsAnalyzing(false);
      
      // Pass results to the parent component
      onAnalysisComplete(analysisResults);
      
      // Reset the analyzer state
      setShowAnalyzer(false);
    }, 2500);
  };
  
  return (
    <div className="space-y-6">
      {!isAnalyzing ? (
        <motion.div 
          className="space-y-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >          
          <EnhancedUrlInput
            value={url}
            onChange={setUrl}
            onSubmit={handleStartAnalysis}
            isLoading={isAnalyzing}
            isValid={urlValid}
          />
          
          <motion.div
            className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 max-w-md mx-auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex items-start">
              <div className="mt-0.5">
                <Network className="w-5 h-5 text-blue-500 dark:text-blue-400 mr-3" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-zinc-800 dark:text-zinc-100 mb-1">
                  What happens during analysis?
                </h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Our AI team analyzes your website content, audience, SEO, market position, 
                  and technical aspects to create a personalized growth strategy.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : (
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* AITeamLoading already has the needed titles, so we can remove the redundant title here */}
          <AITeamLoading 
            progress={progress}
            currentTask={currentTask}
            websiteUrl={urlToAnalyze}
            forceComplete={isComplete}
            // Let the component manage its own timing
            manualControl={true}
          />
        </motion.div>
      )}
      
      {/* Only render AnalyzeStep when needed, in a visually hidden container */}
      {showAnalyzer && (
        <div style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', opacity: 0 }}aria-hidden="true">
          <AnalyzeStep
            onAnalysisComplete={handleAnalysisCompleteFromStep}
            onAnalysisStart={() => {}}// We handle this ourselves
            projectId={projectId}
            initialUrl={urlToAnalyze}
            isOnboarding={true}
          />
        </div>
      )}
    </div>
  );
};

// Function to safely get nested values with type safety
const safeGetNestedValue = <T extends unknown>(
  obj: any,
  path: string,
  defaultValue: T
): T => {
  if (!obj) return defaultValue;
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined || typeof value !== 'object') {
      return defaultValue;
    }
    value = value[key];
  }
  
  return (value === undefined || value === null) 
    ? defaultValue 
    : (value as T);
};

// World-class Growth Insights Component with impressive visualizations
const EnhancedGrowthInsights = ({ results, projectId, onInsightsComplete, onInsightsStateChange, onPageChange }: { results?: any, projectId?: string, onInsightsComplete?: () => void, onInsightsStateChange?: (completed: boolean) => void, onPageChange?: (pageTitle: string) => void }) => {
  // Changed from activeTab to activePage to reflect the new navigation model
  const [activePage, setActivePage] = useState(0);
  const [dbData, setDbData] = useState<any>(null);
  const [isLoadingDb, setIsLoadingDb] = useState(false);
  const [insightsCompleted, setInsightsCompleted] = useState(false);
  const { toast }= useToast();
  
  // Define the pages (formerly tabs) in sequence
  const pages = [
    { id: "market", label: "Market", icon: BarChart3, gradient: "from-emerald-600 to-teal-600" },
    { id: "audience", label: "Audience", icon: Users, gradient: "from-blue-600 to-blue-600" },
    { id: "product", label: "Product", icon: Package, gradient: "from-blue-600 to-blue-800" },
    { id: "keywords", label: "Keywords", icon: Hash, gradient: "from-amber-600 to-orange-600" }
  ];
  
  // Get current page
  const currentPage = pages[activePage];
  
  // Notify parent when page changes
  useEffect(() => {
    if (onPageChange && currentPage) {
      onPageChange(`${currentPage.label} Analysis`);
    }
  }, [activePage, onPageChange, currentPage]);
  
  // If results are directly passed, use them - otherwise fetch from database
  // This ensures we use passed results from the analysis step when available
  const crawlData = results || dbData;
  
  // Navigation controls
  const goToNextPage = () => {
    if (activePage < pages.length - 1) {
      setActivePage(activePage + 1);
      // Scroll to top of the content when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // User has completed all analysis pages
      setInsightsCompleted(true);
      if (onInsightsStateChange) {
        onInsightsStateChange(true);
      }
      if (onInsightsComplete) {
        onInsightsComplete();
      }
    }
  };
  
  const goToPrevPage = () => {
    if (activePage > 0) {
      setActivePage(activePage - 1);
      // Reset completion state when going back
      setInsightsCompleted(false);
      if (onInsightsStateChange) {
        onInsightsStateChange(false);
      }
      // Scroll to top of the content when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Only fetch from database if results aren't provided but projectId is
  useEffect(() => {
    const fetchCrawlData = async () => {
      // Skip fetching if results were directly provided
      if (results) return;
      
      // Only attempt to fetch if we have a projectId
      if (!projectId) {
        console.error("No projectId or results provided to EnhancedGrowthInsights");
        return;
      }
      
      try {
        setIsLoadingDb(true);
        
        // Fetch the latest completed crawl for this project
        const { data: crawl, error }= await supabase
          .from('website_crawls')
          .select('analysis_results')
          .eq('project_id', projectId)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (error) {
          console.error('Error fetching crawl data:', error);
          toast({
            title: "Error",
            description: "Failed to load analysis results",
            variant: "destructive",
          });
          return;
        }
        
        if (!crawl || !crawl.analysis_results) {
          console.log('No completed crawl or analysis results found for project', projectId);
          return;
        }
        
        // Debug log to check the shape of the data
        console.log('Fetched data structure:', crawl);
        if (crawl.analysis_results) {
          console.log('Analysis results keys:', Object.keys(crawl.analysis_results));
        }
        
        // Set only the analysis_results to dbData, not the entire crawl object
        setDbData(crawl.analysis_results);
      }catch (error) {
        console.error('Error in fetchCrawlData:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred while loading results",
          variant: "destructive",
        });
      }finally {
        setIsLoadingDb(false);
      }
    };
    
    fetchCrawlData();
  }, [projectId, results, toast]);
  
  // Enhanced null/undefined check for results
  if (!crawlData && !isLoadingDb) {
    return (
      <div className="p-6 bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-800/80 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-md">
        <div className="flex flex-col items-center justify-center text-center py-8">
          <div className="w-16 h-16 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center mb-4">
            <FileQuestion className="h-8 w-8 text-zinc-400" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-zinc-100">No Analysis Results</h3>
          <p className="text-zinc-600 dark:text-zinc-400 max-w-md">
          No analysis results available. Please complete the analysis step first.
        </p>
        </div>
      </div>
    );
  }

  // Show loading state during database fetch
  if (isLoadingDb) {
    return (
      <div className="relative rounded-xl bg-gradient-to-br from-[#F8F9FF] via-[#F3F5FF] to-white dark:from-zinc-900 dark:via-zinc-900/95 dark:to-zinc-800 p-8 shadow-md overflow-hidden min-h-[300px]">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-200 dark:bg-blue-900 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-blue-200 dark:bg-blue-900 rounded-full opacity-20 blur-3xl"></div>
        
        <div className="flex flex-col items-center justify-center h-full min-h-[240px] relative z-10">
          <div className="animate-spin mb-4">
            <Loader className="h-10 w-10 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-xl font-semibold text-zinc-700 dark:text-zinc-300">
            Loading Growth Insights...
          </h3>
          <p className="text-zinc-500 dark:text-zinc-400 mt-2 text-center max-w-md">
            We're preparing your analysis results with powerful visualizations
          </p>
        </div>
      </div>
    );
  }

  // Validate that crawlData is an object before proceeding
  if (!crawlData || typeof crawlData !== 'object') {
    console.error("Invalid crawl data format:", crawlData);
    return (
      <div className="p-6 bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-800/80 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-md">
        <div className="flex flex-col items-center justify-center text-center py-8">
          <div className="w-16 h-16 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-400" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-zinc-100">Data Format Error</h3>
          <p className="text-zinc-600 dark:text-zinc-400 max-w-md">
            The analysis data is in an unexpected format. Please try running the analysis again.
          </p>
        </div>
      </div>
    );
  }

  // Simple safe getter for nested properties
  const safeGetNestedValue = (obj: any, path: string, defaultValue: any = null) => {
    if (!obj) return defaultValue;
    
    try {
      const keys = path.split('.');
      let result = obj;
      
      for (let key of keys) {
        if (result === null || result === undefined || typeof result !== 'object') {
          return defaultValue;
        }
        result = result[key];
      }
      
      return (result === null || result === undefined) ? defaultValue : result;
    }catch (e) {
      console.error(`Error accessing path ${path}:`, e);
      return defaultValue;
    }
  };
  
  // Function to get color based on audience index
  const getAudienceBarColor = (index: number) => {
    const colors = [
      "from-blue-500 to-blue-400",
      "from-blue-500 to-blue-400",
      "from-blue-500 to-blue-400",
      "from-teal-500 to-teal-400",
      "from-emerald-500 to-emerald-400"
    ];
    return colors[index % colors.length];
  };

  // Function to get height for audience visualization bars
  const getRandomHeight = (index: number) => {
    // Consistent heights based on index
    const heights = [140, 100, 160, 120, 80, 110];
    return heights[index % heights.length];
  };

  // Function to get percentage for audience match
  const getRandomPercentage = (index: number) => {
    // Consistent percentages based on index
    const percentages = [89, 74, 92, 81, 68, 77];
    return percentages[index % percentages.length];
  };

  // Function to get all keywords combined for the keyword cloud
  const getAllKeywords = () => {
    const allKeywords: { keyword: string; type: string; weight: number }[] = [];
    
    // Safely add keywords if they exist, handling both camelCase and snake_case
    if (Array.isArray(coreKeywords) && coreKeywords.length > 0) {
      coreKeywords.forEach((keyword, i) => {
        allKeywords.push({ keyword, type: 'core', weight: 1.0 - (Math.min(i, 10) * 0.05) });
      });
    }
    
    if (Array.isArray(competitiveKeywords) && competitiveKeywords.length > 0) {
      competitiveKeywords.forEach((keyword, i) => {
        allKeywords.push({ keyword, type: 'competitive', weight: 0.9 - (Math.min(i, 10) * 0.05) });
      });
    }
    
    if (Array.isArray(nicheKeywords) && nicheKeywords.length > 0) {
      nicheKeywords.forEach((keyword, i) => {
        allKeywords.push({ keyword, type: 'niche', weight: 0.8 - (Math.min(i, 10) * 0.05) });
      });
    }
    
    if (Array.isArray(longTailKeywords) && longTailKeywords.length > 0) {
      longTailKeywords.forEach((keyword, i) => {
        allKeywords.push({ keyword, type: 'longTail', weight: 0.7 - (Math.min(i, 10) * 0.05) });
      });
    }
    
    // Shuffle and limit to prevent overwhelming visualizations
    return allKeywords.length > 0 
      ? allKeywords
          .sort(() => Math.random() - 0.5)
          .slice(0, Math.min(allKeywords.length, 25))
      : [];
  };

  // Function to get color based on keyword type for the keyword cloud
  const getKeywordTypeColor = (type: string) => {
    switch(type) {
      case 'core':
        return 'bg-gradient-to-r from-teal-600 to-teal-500';
      case 'competitive':
        return 'bg-gradient-to-r from-blue-600 to-blue-500';
      case 'niche':
        return 'bg-gradient-to-r from-blue-600 to-blue-500';
      case 'longTail':
        return 'bg-gradient-to-r from-emerald-600 to-emerald-500';
      default:
        return 'bg-gradient-to-r from-blue-600 to-blue-500';
    }
  };

  // Extract data with improved validation - handle both snake_case and camelCase keys from the database
  const description = safeGetNestedValue(crawlData, 'description', "No product description available.");
  
  // For arrays, ensure they're valid arrays with defensive checks
  const rawFeatures = crawlData?.features || [];
  const features = Array.isArray(rawFeatures) ? rawFeatures : [];
  
  const rawTargetAudience = crawlData?.targetAudience || crawlData?.target_audience || [];
  const targetAudience = Array.isArray(rawTargetAudience) ? rawTargetAudience : [];
  
  // For market analysis, try both camelCase and snake_case
  const marketAnalysis = (() => {
    // Choose data source based on availability and type checking
    const source = (crawlData?.marketAnalysis && typeof crawlData.marketAnalysis === 'object') 
                 ? crawlData.marketAnalysis 
                 : (crawlData?.market_analysis && typeof crawlData.market_analysis === 'object')
                   ? crawlData.market_analysis 
                   : { };
                 
    // Return an object with all the properties we access to prevent undefined errors
    return {
      positioning: source.positioning || {},
      competitiveLandscape: source.competitiveLandscape || source.competitive_landscape || {},
      competitive_landscape: typeof source.competitive_landscape === 'string' ? source.competitive_landscape : '',
      targetMarket: source.targetMarket || source.target_market || {},
      target_market: {
        primary_audience: source.target_market?.primary_audience || '',
        secondary_audience: source.target_market?.secondary_audience || '',
        // Include industry_focus to fix linter errors
        industry_focus: source.target_market?.industry_focus || { industry: [] },
        // Convert industry_focus.industry array to segments format if segments doesn't exist
        segments: source.target_market?.segments || 
                  (source.target_market?.industry_focus?.industry && Array.isArray(source.target_market.industry_focus.industry)) 
                    ? source.target_market.industry_focus.industry.map((industry: string) => ({
                        name: industry,
                        description: `Industry focus: ${industry}`
                      }))
                    : []
      }
    };
  })();
  
  // For keywords, handle both camelCase and snake_case with robust fallbacks
  const keywords = crawlData?.keywords || {};
  const coreKeywords = Array.isArray(keywords?.core) ? keywords.core : [];
  const competitiveKeywords = Array.isArray(keywords?.competitive) ? keywords.competitive : [];
  const nicheKeywords = Array.isArray(keywords?.niche) ? keywords.niche : [];
  const longTailKeywords = Array.isArray(keywords?.longTail || keywords?.long_tail) ? 
                         (keywords.longTail || keywords.long_tail) : [];
  
  // For brand positioning with robust fallback
  const rawBrandPositioning = crawlData?.brandPositioning || crawlData?.brand_positioning || [];
  const brandPositioning = Array.isArray(rawBrandPositioning) ? rawBrandPositioning : [];
  
  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Status indicator with page number */}
      <div className="bg-white/80 dark:bg-zinc-800/50 p-3 rounded-lg border border-zinc-200 dark:border-zinc-700 backdrop-blur-sm">
        <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {isLoadingDb ? "Loading data..." : `Viewing: ${currentPage.label} Analysis (${activePage + 1}/${pages.length})`}
          </p>
      </div>

          {/* Page dots indicator */}
          <div className="flex gap-1.5">
            {pages.map((page, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activePage 
                    ? "bg-blue-500 scale-125" 
                    : "bg-zinc-300 dark:bg-zinc-700"
                }`}
              />
            ))}
        </div>
      </div>
                  </div>
              
      {/* Content for the current page */}
      <AnimatePresence mode="wait">
              <motion.div 
          key={`page-${activePage}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
        >
          {/* Market Page Content */}
          {currentPage.id === "market" && (
            <div className="space-y-6" id="insights-market-page">
              {/* Market Analysis Card - Moved to be first */}
          <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                <div className="bg-gradient-to-br from-blue-500 to-teal-600 rounded-full p-2.5 shadow-sm">
                  <BarChart3 className="h-5 w-5 text-white" />
                    </div>
                <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-teal-700 bg-clip-text text-transparent">
                      Market Analysis
                    </h3>
                  </div>
                  
              <div className="grid gap-6 sm:grid-cols-2">
                    {/* Market Positioning */}
                <motion.div 
                  className="bg-gradient-to-br from-blue-50 to-zinc-50 dark:from-blue-900/20 dark:to-zinc-800 p-5 rounded-lg border border-blue-100 dark:border-blue-800/30 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                      <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900/50">
                        <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                        <h4 className="font-medium text-lg text-zinc-800 dark:text-zinc-200">
                          Market Positioning
                        </h4>
                      </div>

                      {marketAnalysis.positioning && typeof marketAnalysis.positioning === 'object' && 
                       (marketAnalysis.positioning.value_proposition || 
                        marketAnalysis.positioning.price_point || 
                        marketAnalysis.positioning.market_segment) ? (
                        <div className="space-y-3">
                          {marketAnalysis.positioning.price_point && (
                            <div className="flex items-start gap-2">
                              <div className="p-1 mt-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                                <DollarSign className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Price Point</div>
                                <div className="text-zinc-600 dark:text-zinc-400">
                                  {marketAnalysis.positioning.price_point || 'N/A'}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {marketAnalysis.positioning.market_segment && (
                            <div className="flex items-start gap-2">
                              <div className="p-1 mt-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                                <Users2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Target Market</div>
                                <div className="text-zinc-600 dark:text-zinc-400">
                                  {marketAnalysis.positioning.market_segment || 'N/A'}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {marketAnalysis.positioning.value_proposition && (
                            <div className="flex items-start gap-2">
                              <div className="p-1 mt-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                                <Star className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Value Proposition</div>
                                <div className="text-zinc-600 dark:text-zinc-400">
                                  {marketAnalysis.positioning.value_proposition || 'N/A'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-zinc-600 dark:text-zinc-400">
                          {typeof marketAnalysis.positioning === 'string' 
                            ? marketAnalysis.positioning 
                            : "No market positioning data available."}
                        </p>
                      )}

                  {/* Market size */}
                  {marketAnalysis.positioning?.market_size && (
                    <div className="mt-4 pt-4 border-t border-blue-100 dark:border-blue-800/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Market Size</span>
                        <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                          {marketAnalysis.positioning.market_size}
                        </span>
                    </div>
                      <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5">
                        <motion.div 
                          className="bg-gradient-to-r from-blue-500 to-teal-500 h-2.5 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: "85%" }}
                          transition={{ duration: 1, delay: 0.5 }}
                        ></motion.div>
                      </div>
                    </div>
                  )}
                </motion.div>
                    
                    {/* Competitive Landscape */}
                <motion.div 
                  className="bg-gradient-to-br from-orange-50 to-zinc-50 dark:from-orange-900/20 dark:to-zinc-800 p-5 rounded-lg border border-orange-100 dark:border-orange-800/30 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                      <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-full bg-orange-100 dark:bg-orange-900/50">
                        <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                        <h4 className="font-medium text-lg text-zinc-800 dark:text-zinc-200">
                          Competitive Landscape
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {marketAnalysis.competitiveLandscape && marketAnalysis.competitiveLandscape.advantages?.item 
                          ? (
                            <>
                              {marketAnalysis.competitiveLandscape.advantages.item.map((item, idx) => (
                                <div key={idx}className="flex items-start gap-2">
                                  <div className="p-1 mt-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex-shrink-0">
                                    <span className="block h-1.5 w-1.5 rounded-full bg-orange-500"></span>
                                  </div>
                                  <span className="text-zinc-700 dark:text-zinc-300 text-sm">{item}</span>
                                </div>
                              ))}
                            </>
                          ) 
                          : typeof marketAnalysis.competitiveLandscape === 'string' || typeof marketAnalysis.competitive_landscape === 'string' 
                            ? (
                              <>
                                {(marketAnalysis.competitiveLandscape || marketAnalysis.competitive_landscape || "")
                                  .split('. ')
                                  .filter(sentence => sentence.trim().length > 0)
                                  .map((sentence, idx) => (
                                    <div key={idx}className="flex items-start gap-2">
                                      <div className="p-1 mt-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex-shrink-0">
                                        <span className="block h-1.5 w-1.5 rounded-full bg-orange-500"></span>
                                      </div>
                                      <span className="text-zinc-700 dark:text-zinc-300 text-sm">{sentence.trim()}</span>
                                    </div>
                                  ))
                                }
                              </>
                            ) 
                            : (
                              <p className="text-zinc-600 dark:text-zinc-400">No competitive landscape data available.</p>
                            )
                        }
                      </div>

                  {/* Competition level */}
                  {marketAnalysis.competitiveLandscape?.competition_level && (
                    <div className="mt-4 pt-4 border-t border-orange-100 dark:border-orange-800/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Competition Level</span>
                        <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                          {marketAnalysis.competitiveLandscape.competition_level}
                        </span>
                    </div>
                      <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5">
                        <motion.div 
                          className="bg-gradient-to-r from-orange-500 to-amber-500 h-2.5 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: "65%" }}
                          transition={{ duration: 1, delay: 0.6 }}
                        ></motion.div>
                      </div>
                    </div>
                  )}
                </motion.div>
                
                {/* Target Market */}
                <motion.div 
                  className="bg-gradient-to-br from-blue-50 to-zinc-50 dark:from-blue-900/20 dark:to-zinc-800 p-5 rounded-lg border border-blue-100 dark:border-blue-800/30 shadow-sm sm:col-span-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900/50">
                      <Users2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h4 className="font-medium text-lg text-zinc-800 dark:text-zinc-200">
                      Target Market
                    </h4>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Primary Audience */}
                    <div className="bg-white dark:bg-zinc-800 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <h5 className="font-medium text-base text-blue-700 dark:text-blue-300">Primary Audience</h5>
                      </div>
                      <p className="text-zinc-600 dark:text-zinc-300 ml-5 text-sm">
                        {marketAnalysis.target_market?.primary_audience || "Business professionals seeking efficient solutions"}
                      </p>
                    </div>
                    
                    {/* Secondary Audience */}
                    <div className="bg-white dark:bg-zinc-800 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <h5 className="font-medium text-base text-blue-700 dark:text-blue-300">Secondary Audience</h5>
                      </div>
                      <p className="text-zinc-600 dark:text-zinc-300 ml-5 text-sm">
                        {marketAnalysis.target_market?.secondary_audience || "Small to medium businesses looking for scalable solutions"}
                      </p>
                    </div>
                    
                    {/* Industry Focus */}
                    <div className="bg-white dark:bg-zinc-800 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <h5 className="font-medium text-base text-blue-700 dark:text-blue-300">Industry Focus</h5>
                      </div>
                      <div className="ml-5">
                        {marketAnalysis.target_market?.industry_focus?.industry && 
                         Array.isArray(marketAnalysis.target_market.industry_focus.industry) && 
                         marketAnalysis.target_market.industry_focus.industry.length > 0 ? (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {marketAnalysis.target_market.industry_focus.industry.map((industry, idx) => (
                              <span key={idx}className="text-xs px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30">
                                {industry}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-zinc-600 dark:text-zinc-300 text-sm">
                            Technology, Finance, Healthcare, Education
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
                  </div>
                </CardContent>
              </Card>

              {/* Brand Positioning Card */}
          <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full p-2.5 shadow-sm">
                  <Globe className="h-5 w-5 text-white" />
                    </div>
                <h3 className="text-xl font-semibold bg-gradient-to-r from-teal-600 to-emerald-700 bg-clip-text text-transparent">
                      Brand Positioning
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                {brandPositioning.length > 0 ? (
                  brandPositioning.map((position, index) => (
                        <motion.div 
                          key={index}
                      className="p-5 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-lg border border-teal-100 dark:border-teal-800/30 shadow-sm"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(index * 0.1, 0.5) }}
                      whileHover={{ y: -3, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" }}
                    >
                      <h4 className="font-medium text-lg text-zinc-900 dark:text-white mb-2 flex items-center">
                        <div className="p-1 rounded-full bg-teal-100 dark:bg-teal-900/30 mr-2">
                          <Compass className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                        </div>
                        {position.aspect || position.title || "Brand Aspect"}
                          </h4>
                      <p className="text-zinc-600 dark:text-zinc-300 ml-8">
                        {position.description || position.content || "No description available."}
                          </p>
                        </motion.div>
                      ))
                    ) : (
                  <motion.div 
                    className="p-6 text-center bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-800/60 rounded-lg border border-zinc-200 dark:border-zinc-700"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-teal-100 to-emerald-100 dark:from-teal-900/30 dark:to-emerald-900/30 flex items-center justify-center">
                      <FileQuestion className="w-8 h-8 text-teal-400 dark:text-teal-500" />
                    </div>
                    <h4 className="font-medium text-lg text-zinc-900 dark:text-white mb-2">No Brand Positioning Data</h4>
                    <p className="text-zinc-500 dark:text-zinc-400">
                      We couldn't find any brand positioning information for this product.
                    </p>
                  </motion.div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          {/* Audience Page Content */}
          {currentPage.id === "audience" && (
            <div className="space-y-6" id="insights-audience-page">
              {/* Target Audience Card */}
              <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-full p-2.5 shadow-sm">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                      Target Audience Segments
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {targetAudience.length > 0 ? (
                      targetAudience.map((audience, index) => (
                        <motion.div 
                          key={index}
                          className="p-5 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30 shadow-sm"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(index * 0.1, 0.5) }}
                          whileHover={{ y: -3, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" }}
                        >
                          <h4 className="font-medium text-lg text-zinc-900 dark:text-white mb-2 flex items-center">
                            <div className="p-1 rounded-full bg-blue-100 dark:bg-blue-900/30 mr-2">
                              <Target className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            {audience.segment}
                          </h4>
                          <p className="text-zinc-600 dark:text-zinc-300 ml-8">
                            {audience.description}
                          </p>

                          {/* Audience characteristics tags */}
                          {audience.characteristics && Array.isArray(audience.characteristics) && (
                            <div className="flex flex-wrap gap-2 mt-3 ml-8">
                              {audience.characteristics.map((characteristic, idx) => (
                                <motion.span 
                                  key={idx}
                                  className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700/30"
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: Math.min(index * 0.1 + 0.2 + (idx * 0.05), 1) }}
                                >
                                  {characteristic}
                                </motion.span>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      ))
                    ) : (
                      <motion.div 
                        className="p-6 text-center bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-800/60 rounded-lg border border-zinc-200 dark:border-zinc-700"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 flex items-center justify-center">
                          <Users className="w-8 h-8 text-blue-400 dark:text-blue-500" />
                        </div>
                        <h4 className="font-medium text-lg text-zinc-900 dark:text-white mb-2">No Audience Data</h4>
                        <p className="text-zinc-500 dark:text-zinc-400">
                          We couldn't find any target audience information.
                        </p>
            </motion.div>
          )}
                  </div>
                </CardContent>
              </Card>

              {/* Audience Visualization Card (if data available) */}
              {targetAudience.length > 0 && (
                <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-full p-2.5 shadow-sm">
                        <PieChart className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                        Audience Distribution
                      </h3>
                    </div>
                    
            <motion.div 
                      className="relative h-60 mt-4 rounded-lg overflow-hidden bg-gradient-to-br from-zinc-50 to-blue-50/30 dark:from-zinc-800 dark:to-blue-900/10 p-4 border border-zinc-200 dark:border-zinc-700"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 240 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex space-x-8 h-full items-end pt-10">
                          {targetAudience.map((audience, index) => (
                            <motion.div 
                              key={index}
                              className="flex flex-col items-center"
                              initial={{ opacity: 0, y: 50 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
                            >
                              <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2 whitespace-nowrap max-w-[100px] truncate">
                                {audience.segment}
                              </div>
                              <motion.div 
                                className={`w-16 bg-gradient-to-t ${getAudienceBarColor(index)}rounded-t-lg relative group`}
                                initial={{ height: 0 }}
                                animate={{ height: `${getRandomHeight(index)}px` }}
                                transition={{ duration: 0.7, delay: 0.6 + (index * 0.1), ease: "easeOut" }}
                              >
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-zinc-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                  {getRandomPercentage(index)}% match
                                </div>
                              </motion.div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                    
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-2 text-center">
                      Audience segment distribution based on content analysis
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          {/* Product Page Content */}
          {currentPage.id === "product" && (
            <div className="space-y-6" id="insights-product-page">
              {/* Product Description Card */}
              <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-full p-2.5 shadow-sm">
                      <Package className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                      Product Overview
                    </h3>
                  </div>
                  
                  <motion.div 
                    className="prose dark:prose-invert max-w-none"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                      {description}
                    </p>
                  </motion.div>
                </CardContent>
              </Card>

              {/* Features Card */}
              <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-full p-2.5 shadow-sm">
                      <Star className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold bg-gradient-to-r from-emerald-600 to-green-700 bg-clip-text text-transparent">
                      Key Features & Benefits
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {features.length > 0 ? (
                      features.map((feature, index) => (
                        <motion.div 
                          key={index}
                          className="p-5 bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/60 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-sm"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 + 0.2 }}
                        >
                          <h4 className="font-medium text-lg text-zinc-900 dark:text-white mb-2 flex items-center">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></div>
                            {feature.name}
                          </h4>
                          <ul className="space-y-2">
                            {feature.items && feature.items.map((item, itemIndex) => (
                              <motion.li 
                                key={itemIndex}
                                className="flex gap-2"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: (index * 0.1) + 0.2 + (itemIndex * 0.05) }}
                              >
                                <div className="p-1 mt-0.5 rounded-full bg-green-100 dark:bg-green-900/30 flex-shrink-0">
                                  <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
                                </div>
                                <span className="text-zinc-700 dark:text-zinc-300">{item}</span>
                              </motion.li>
                            ))}
                          </ul>
                        </motion.div>
                      ))
                    ) : (
                      <motion.div 
                        className="p-6 text-center bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-800/60 rounded-lg border border-zinc-200 dark:border-zinc-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center">
                          <FileX className="w-8 h-8 text-zinc-400" />
                        </div>
                        <h4 className="font-medium text-lg text-zinc-900 dark:text-white mb-2">No Feature Information</h4>
                        <p className="text-zinc-500 dark:text-zinc-400">
                          We couldn't find any feature details for this product.
                        </p>
                      </motion.div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          {/* Keywords Page Content */}
          {currentPage.id === "keywords" && (
            <div className="space-y-6" id="insights-keywords-page">
          <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-full p-2.5 shadow-sm">
                  <Search className="h-5 w-5 text-white" />
                    </div>
                <h3 className="text-xl font-semibold bg-gradient-to-r from-amber-600 to-orange-700 bg-clip-text text-transparent">
                      Keywords Analysis
                    </h3>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Core Keywords */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 rounded-full bg-teal-500" />
                        <h4 className="font-medium text-lg text-teal-700 dark:text-teal-300">
                          Core Keywords
                        </h4>
                        <ChevronRight className="h-5 w-5 text-teal-400 dark:text-teal-300" />
                      </div>
                      <div className="mb-4 p-3 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50/50 dark:bg-zinc-800/30">
                        <ul className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-1">
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Foundation terms (1-2 words) directly related to your main business offerings like 'SEO tools' or 'marketing software'</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>These broad, competitive keywords establish topical authority and brand awareness</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Essential for capturing high-volume searches but require strong domain authority to rank</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Perfect for homepage optimization and brand building campaigns</span>
                          </li>
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {coreKeywords.length > 0 ? (
                          coreKeywords.map((keyword, index) => (
                            <motion.span 
                              key={index}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-teal-100 text-teal-800 border border-teal-200 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-800/50 shadow-sm hover:shadow-md cursor-pointer transform hover:-translate-y-0.5 transition-all duration-150"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: Math.min(index * 0.05, 0.5) }}
                              whileHover={{ y: -2, scale: 1.05, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                            >
                              {keyword}
                            </motion.span>
                          ))
                        ) : (
                          <p className="text-zinc-500 dark:text-zinc-400 italic">
                            No core keywords available.
                          </p>
                        )}
                      </div>
                    </motion.div>
                    
                    {/* Competitive Keywords */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 rounded-full bg-blue-500" />
                        <h4 className="font-medium text-lg text-blue-700 dark:text-blue-300">
                          Competitive Keywords
                        </h4>
                        <ChevronRight className="h-5 w-5 text-blue-400 dark:text-blue-300" />
                      </div>
                      <div className="mb-4 p-3 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50/50 dark:bg-zinc-800/30">
                        <ul className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-1">
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Keywords your competitors currently rank for, revealing market opportunities and content gaps</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Use these for competitor analysis, identifying trending topics in your industry, and capturing competitor traffic</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Valuable for PPC campaigns to intercept competitor searches and for content strategy to target underserved market segments</span>
                          </li>
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {competitiveKeywords.length > 0 ? (
                          competitiveKeywords.map((keyword, index) => (
                            <motion.span 
                              key={index}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/50 shadow-sm hover:shadow-md cursor-pointer transform hover:-translate-y-0.5 transition-all duration-150"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.3 + Math.min(index * 0.05, 0.5) }}
                              whileHover={{ y: -2, scale: 1.05, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                            >
                              {keyword}
                            </motion.span>
                          ))
                        ) : (
                          <p className="text-zinc-500 dark:text-zinc-400 italic">
                            No competitive keywords available.
                          </p>
                        )}
                      </div>
                    </motion.div>
                    
                    {/* Niche Keywords */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 rounded-full bg-blue-500" />
                        <h4 className="font-medium text-lg text-blue-700 dark:text-blue-300">
                          Niche & Feature-Based Keywords
                        </h4>
                        <ChevronRight className="h-5 w-5 text-blue-400 dark:text-blue-300" />
                      </div>
                      <div className="mb-4 p-3 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50/50 dark:bg-zinc-800/30">
                        <ul className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-1">
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Specialized terms targeting unique features or specific market segments like 'AI-powered analytics dashboard' or 'GDPR-compliant email tools'</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Less competitive with higher relevance to specific user needs</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Ideal for targeting precise audiences, product pages, and establishing expertise in specialized areas</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Great for conversion-focused content</span>
                          </li>
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {nicheKeywords.length > 0 ? (
                          nicheKeywords.map((keyword, index) => (
                            <motion.span 
                              key={index}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/50 shadow-sm hover:shadow-md cursor-pointer transform hover:-translate-y-0.5 transition-all duration-150"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.4 + Math.min(index * 0.05, 0.5) }}
                              whileHover={{ y: -2, scale: 1.05, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                            >
                              {keyword}
                            </motion.span>
                          ))
                        ) : (
                          <p className="text-zinc-500 dark:text-zinc-400 italic">
                            No niche keywords available.
                          </p>
                        )}
                      </div>
                    </motion.div>
                
                {/* Long-tail Keywords */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 rounded-full bg-emerald-500" />
                        <h4 className="font-medium text-lg text-emerald-700 dark:text-emerald-300">
                          Long-Tail Keywords
                        </h4>
                        <ChevronRight className="h-5 w-5 text-emerald-400 dark:text-emerald-300" />
                      </div>
                      <div className="mb-4 p-3 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50/50 dark:bg-zinc-800/30">
                        <ul className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-1">
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Longer, specific phrases (3-5+ words) like 'best SEO tools for small businesses' or 'how to improve website ranking 2024'</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Lower search volume but higher conversion rates and easier to rank for</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                            <span>Perfect for voice search optimization, FAQ content, blog posts, and cost-effective PPC campaigns with better ROI</span>
                          </li>
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {longTailKeywords.length > 0 ? (
                          longTailKeywords.map((keyword, index) => (
                            <motion.span 
                              key={index}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800/50 shadow-sm hover:shadow-md cursor-pointer transform hover:-translate-y-0.5 transition-all duration-150"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.5 + Math.min(index * 0.05, 0.5) }}
                              whileHover={{ y: -2, scale: 1.05, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                            >
                              {keyword}
                            </motion.span>
                          ))
                        ) : (
                          <p className="text-zinc-500 dark:text-zinc-400 italic">
                            No long-tail keywords available.
                          </p>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </CardContent>
              </Card>

          {/* Keyword Cloud Visualization */}
          {(coreKeywords.length > 0 || competitiveKeywords.length > 0 || nicheKeywords.length > 0 || longTailKeywords.length > 0) && (
            <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700 shadow-md bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-800/80">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-full p-2.5 shadow-sm">
                    <Hash className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                    Keyword Cloud
                  </h3>
                </div>
                
                <motion.div 
                  className="p-6 min-h-[200px] bg-gradient-to-br from-zinc-50 to-blue-50/30 dark:from-zinc-900 dark:to-blue-900/10 rounded-lg border border-zinc-200 dark:border-zinc-700 relative overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] dark:bg-[radial-gradient(#1e293b_1px,transparent_1px)] opacity-20"></div>
                  
                  <div className="flex flex-wrap justify-center relative z-10">
                    {getAllKeywords().map((item, index) => (
                      <motion.div
                        key={index}
                        className={`m-2 px-3 py-1.5 rounded-lg text-white font-medium shadow-sm ${getKeywordTypeColor(item.type)}`}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ 
                          delay: 0.6 + (index * 0.03), 
                          type: "spring",
                          stiffness: 200,
                          damping: 15
                        }}
                        style={{ 
                          fontSize: `${Math.max(0.8, 0.8 + (item.weight * 0.7))}rem`,
                        }}
                        whileHover={{ 
                          scale: 1.1, 
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                          rotate: index % 2 === 0 ? 3 : -3
                        }}
                      >
                        {item.keyword}
            </motion.div>
                    ))}
                  </div>
                </motion.div>
                <div className="mt-4 flex justify-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-teal-500 to-teal-600"></div>
                    <span className="text-xs text-zinc-600 dark:text-zinc-400">Core</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                    <span className="text-xs text-zinc-600 dark:text-zinc-400">Competitive</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                    <span className="text-xs text-zinc-600 dark:text-zinc-400">Niche</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600"></div>
                    <span className="text-xs text-zinc-600 dark:text-zinc-400">Long-tail</span>
                  </div>
                </div>
              </CardContent>
            </Card>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation buttons */}
      <div className="flex justify-between mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={goToPrevPage}
          disabled={activePage === 0}
          className="border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
        
        <Button
          type="button"
          onClick={goToNextPage} // Simplified: always call goToNextPage, it handles completion
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {activePage < pages.length - 1 ? (
            <>
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </>
          ) : (
            <>
              Complete
              <Check className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

// Add this component before the OnboardingWizard component
// Journey to 10K MRR Component
export const JourneyToMRR = ({ 
  companyName = "your company",
  onStartJourneyClick
}: { 
  companyName?: string;
  onStartJourneyClick?: () => void;
}) => {
  const [hoverButton, setHoverButton] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [readProgress, setReadProgress] = useState(0);
  const [hoverStage, setHoverStage] = useState<number | null>(null);
  const companyNameSafe = companyName || "your company";
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Track if component is mounted for cleanup
  const isMounted = useRef(true);
  
  // Respect user's motion preferences
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
  
  // Industry-specific gradient - would be dynamic based on the industry in a real implementation
  const industryGradient = "from-blue-500 via-blue-500 to-blue-500";
  
  // Generate particles for the flowing path animation
  const flowParticles = useMemo(() => 
    Array.from({ length: 12 }).map(() => ({
      size: Math.random() * 4 + 2,
      speed: Math.random() * 8 + 4,
      delay: Math.random() * 5,
      yOffset: Math.random() * 30 - 15
    })),
  []);
  
  // Enhanced milestones with richer data
  const milestones = [
    { 
      label: "Starting Point", 
      value: "Today", 
      icon: MapPin, 
      color: "bg-blue-500",
      subtitle: "Launch", 
      metrics: ["Your foundation", "Product ready", "Execution begins"]
    },
    { 
      label: "First Customer", 
      value: "$1", 
      icon: BadgeCheck, 
      color: "bg-green-500",
      subtitle: "Validation", 
      metrics: ["Product validation", "First sale", "Market feedback"]
    },
    { 
      label: "First Milestone", 
      value: "$1K MRR", 
      icon: Trophy, 
      color: "bg-blue-500",
      subtitle: "Traction", 
      metrics: ["10-50 customers", "Repeatable sales", "Growth signals"]
    },
    { 
      label: "Growth Stage", 
      value: "$5K MRR", 
      icon: TrendingUp, 
      color: "bg-violet-500",
      subtitle: "Momentum", 
      metrics: ["100+ customers", "Scaling systems", "Team expansion"]
    },
    { 
      label: "Target Goal", 
      value: "$10K MRR", 
      icon: Trophy, 
      color: "bg-amber-500/90 shadow-amber-500/20 ring-2 ring-amber-300/50",
      subtitle: "Sustainability", 
      metrics: ["Enterprise ready", "Predictable growth", "Market leadership"]
    },
  ];

  // AI agents that help at each milestone
  const aiAgents = [
    { name: "Research AI", icon: Search, color: "bg-cyan-500", milestone: 0 },
    { name: "Marketing AI", icon: Megaphone, color: "bg-emerald-500", milestone: 1 },
    { name: "Analytics AI", icon: BarChart, color: "bg-blue-500", milestone: 2 },
    { name: "Strategy AI", icon: Lightbulb, color: "bg-blue-500", milestone: 3 },
    { name: "Growth AI", icon: TrendingUp, color: "bg-amber-500", milestone: 4 },
  ];

  // Track scroll position to animate path based on reading progress
  useEffect(() => {
    if (!contentRef.current || prefersReducedMotion) return;
    
    const handleScroll = () => {
      if (!contentRef.current || !isMounted.current) return;
      
      // Calculate how far through the content the user has scrolled
      const { top, height }= contentRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // How much of the element is visible
      const visibleAmount = Math.min(windowHeight, top + height) - Math.max(0, top);
      // Percentage of the element that's visible (0-1)
      const visiblePercentage = Math.max(0, Math.min(1, visibleAmount / height));
      
      setReadProgress(visiblePercentage);
      
      // Update active section based on scroll
      const scrollPercentage = (windowHeight - top) / windowHeight;
      const newActiveSection = Math.floor(scrollPercentage * 4);
      if (newActiveSection >= 0 && newActiveSection <= 4 && newActiveSection !== activeSection) {
        setActiveSection(newActiveSection);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initialize on mount
    handleScroll();
    
    return () => {
      isMounted.current = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeSection, prefersReducedMotion]);

  // State for micro-animations
  const [showBuildingAnimation, setShowBuildingAnimation] = useState(false);
  const [showShieldAnimation, setShowShieldAnimation] = useState(false);
  const [countValue, setCountValue] = useState(0);
  
  // Trigger micro-animations based on active section
  useEffect(() => {
    if (prefersReducedMotion) {
      // Skip animations for users who prefer reduced motion
      setCountValue(10);
      return;
    }
    
    if (activeSection >= 0) {
      setShowBuildingAnimation(true);
    }
    if (activeSection >= 2) {
      setShowShieldAnimation(true);
    }
    if (activeSection >= 3) {
      // Start counting animation
      let startTime: number | null = null;
      let requestId: number;
      
      const countToTarget = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / 3000, 1); // 3 seconds duration
        
        if (isMounted.current) {
          setCountValue(Math.floor(progress * 10));
          
          if (progress < 1) {
            requestId = requestAnimationFrame(countToTarget);
          }
        }
      };
      
      requestId = requestAnimationFrame(countToTarget);
      return () => {
        if (requestId) {
          cancelAnimationFrame(requestId);
        }
      };
    }
  }, [activeSection, prefersReducedMotion]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Enhanced dynamic background with subtle patterns */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-50 to-blue-50/30 dark:from-zinc-900 dark:to-blue-900/10 -z-10">
        {/* Abstract decorative patterns */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle,_currentColor_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
        </div>
        
        {/* Enhanced glow effects */}
        <div className="absolute top-40 -right-20 w-80 h-80 bg-blue-500/20 rounded-full blur-[100px] opacity-30"></div>
        <div className="absolute -bottom-40 -left-20 w-80 h-80 bg-blue-500/20 rounded-full blur-[100px] opacity-30"></div>
        <div className="absolute top-20 left-1/3 w-40 h-40 bg-emerald-500/20 rounded-full blur-[80px] opacity-20"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-32 h-32 border border-blue-200/20 dark:border-blue-800/20 rounded-full"></div>
        <div className="absolute bottom-10 left-10 w-24 h-24 border border-blue-200/20 dark:border-blue-800/20 rounded-full"></div>
      </div>

      {/* Main content container with enhanced whitespace */}
      <div ref={contentRef}className="flex flex-col md:flex-row gap-10 p-8 md:p-12">
        {/* Left side: Text presentation with improved spacing */}
        <div className="flex-1 space-y-10">
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl text-zinc-800 dark:text-zinc-200">
              Your Growth Journey
            </h2>
            
            <div className="relative text-zinc-700 dark:text-zinc-300 text-xl leading-relaxed pl-1">
              The path to sustainable growth for <span className="text-zinc-700 dark:text-zinc-300">{companyNameSafe}</span>
            </div>
          </div>
          
          <div className="space-y-8">
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              If you're reading this, chances are you've launched, or you're about to
              launch, and you're asking the same question every founder hits:
            </p>
            
            <div className="text-xl md:text-2xl p-8 bg-zinc-100 dark:bg-zinc-800/60 rounded-lg border-l-4 border-blue-500 shadow-sm my-6">
              <span className="inline-block text-zinc-700 dark:text-zinc-300">
                "How do I actually get people to use {" "}
              </span>
              <span className="inline-block text-zinc-700 dark:text-zinc-300">
                {companyNameSafe}
              </span>
              <span className="inline-block text-zinc-700 dark:text-zinc-300">
                ?
              </span>
            </div>
          </div>
          
          <div className="space-y-8 pl-1 pt-2">
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              What you've unlocked isn't just another guide.
            </p>
            
            <div className="relative text-xl md:text-2xl pl-1 leading-relaxed text-zinc-700 dark:text-zinc-300">
              It's a{" "}
              <span className="relative inline-block text-zinc-700 dark:text-zinc-300">
                growth system powered by AI
              </span>{" "}
              designed for your success.
            </div>
            
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed pl-1">
              No fluff. No guesswork. Just actions that drive growth.
            </p>
          </div>
          
          <div className="space-y-8 pt-6 pl-1">
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              But here's the truth:
            </p>
            
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              These aren't "tricks." They're real strategies that require real execution.
            </p>
            
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              The good news?
            </p>
            
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              You've already done the hard part. You built the product. You showed up.
            </p>
          </div>
          
          <div className="space-y-8 pt-6 pl-1">
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              And if you're like most founders, you've seen the success stories. The tweets. The product launch screenshots.
            </p>
            
            <div className="relative text-xl md:text-2xl pl-1 leading-relaxed text-zinc-700 dark:text-zinc-300">
              The "just hit $10K MRR" posts.
            </div>
            
            <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed">
              You've probably imagined writing your own.
            </p>
          </div>
          
          <div className="pt-6 pl-1">
            <p className="text-xl text-zinc-700 dark:text-zinc-300 leading-relaxed">
              This playbook is your way in. Are you ready?
            </p>
          </div>
          
          {/* CTA Button */}
          <motion.div
            className="pt-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 3 }}
          >
            <motion.button
              className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-5 rounded-lg font-bold text-lg shadow-lg"
              whileHover={{ scale: prefersReducedMotion ? 1 : 1.03 }}
              whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
              onMouseEnter={() => setHoverButton(true)}
              onMouseLeave={() => setHoverButton(false)}
              onClick={onStartJourneyClick}
            >
              {/* Light beam animation */}
              {!prefersReducedMotion && (
                <motion.div 
                  className="absolute top-0 left-0 w-20 h-full bg-white/20 skew-x-[-20deg]"
                  animate={{ 
                    x: ['-100%', '200%'] 
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    repeatDelay: 1.5
                  }}
                  aria-hidden="true"
                />
              )}
              
              {/* Button content with icon transition */}
              <motion.div 
                className="relative z-10 flex items-center"
                animate={hoverButton && !prefersReducedMotion ? { x: -5 }: { x: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span>Start My Journey</span>
                
                <motion.div
                  className="ml-2 transition-all duration-300"
                  animate={hoverButton && !prefersReducedMotion ? { 
                    x: 5, 
                    rotate: 45,
                    scale: 1.2
                  }: {
                    x: 0,
                    rotate: 0,
                    scale: 1
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  {hoverButton && !prefersReducedMotion ? <Rocket className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
                </motion.div>
              </motion.div>
            </motion.button>
          </motion.div>
        </div>
          
        {/* Right side: Advanced AI Journey Visualization */}
        <div className="md:w-2/5 relative">
          <div className="sticky top-24 pt-10 mt-8">
            <div className="relative h-[570px] flex items-center justify-center overflow-visible px-4">
              {/* Background glow effects with improved positioning */}
              <div className="absolute inset-0 overflow-hidden rounded-xl">
                <div className="absolute -top-10 -right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl opacity-30"></div>
                <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl opacity-30"></div>
                <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl opacity-20"></div>
                {/* Add subtle frame/container effect */}
                <div className="absolute inset-0 border border-zinc-200/30 dark:border-zinc-700/30 rounded-xl"></div>
              </div>
              
              {/* Main journey visualization with enhanced depth and AI elements */}
              <div className="relative">
                {/* Vertical glowing path with improved positioning */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-full overflow-hidden shadow-sm">
                  {/* Main path with enhanced gradient */}
                  <div className="w-full h-full bg-gradient-to-b from-blue-500 via-blue-500 to-amber-500"></div>
                  
                  {/* Enhanced animated glow effect */}
                  {!prefersReducedMotion && (
                    <motion.div 
                      className="absolute w-5 h-24 left-1/2 -translate-x-1/2 bg-gradient-to-b from-transparent via-white to-transparent opacity-40 blur-sm"
                      animate={{ top: ["-10%", "95%"] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    />
                  )}
                </div>
                
                {/* Milestone points along the journey */}
                <div className="relative ml-6 space-y-16 pt-4">
                  {milestones.map((milestone, index) => {
                    if (!milestone) return null;
                    const Icon = milestone.icon || Trophy;
                    const isActive = index === activeSection;
                    const isPassed = index < activeSection;
                    const isFuture = index > activeSection;
                    
                    return (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 * index }}
                        className="relative"
                        onMouseEnter={() => setHoverStage(index)}
                        onMouseLeave={() => setHoverStage(null)}
                      >
                        {/* Enhanced connecting line from path to milestone */}
                        <div className="absolute -left-9 top-9 w-10 h-1 bg-gradient-to-r from-blue-400/30 to-blue-400 dark:from-blue-500/30 dark:to-blue-500 rounded-full"></div>
                        
                        <div className="flex items-start">
                          {/* Icon node with enhanced animation and visual treatment */}
                          <motion.div 
                            className={`
                              relative rounded-full flex items-center justify-center z-20
                              ${isActive || hoverStage === index
                                ? 'ring-2 ring-offset-4 ring-offset-white dark:ring-offset-zinc-900 ring-blue-400 shadow-md shadow-blue-500/20' 
                                : 'ring-1 ring-zinc-200 dark:ring-zinc-700/30'}
                              ${isPassed ? 'shadow-lg' : 'shadow-md'}
                            `}
                            animate={isActive && !prefersReducedMotion ? {
                              scale: [1, 1.05, 1],
                              boxShadow: [
                                '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
                                '0 10px 15px -3px rgba(59, 130, 246, 0.2)',
                                '0 4px 6px -1px rgba(59, 130, 246, 0.1)'
                              ]
                            }: { }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <div 
                              className={`
                                w-16 h-16 rounded-full flex items-center justify-center
                                ${milestone.color}
                                ${isPassed ? 'opacity-100' : (isFuture ? 'opacity-80' : 'opacity-100')}
                                ${isActive ? 'ring-2 ring-white dark:ring-zinc-800 shadow-inner' : ''}
                              `}
                            >
                              <Icon className={`w-7 h-7 text-white ${isActive ? 'drop-shadow-md' : ''}`}/>
                              
                              {/* Enhanced pulse effect for active milestone */}
                              {(isActive || hoverStage === index) && !prefersReducedMotion && (
                                <motion.div
                                  className="absolute inset-0 rounded-full"
                                  animate={{
                                    boxShadow: [
                                      '0 0 0 0px rgba(59, 130, 246, 0.4)',
                                      '0 0 0 12px rgba(59, 130, 246, 0)'
                                    ]
                                  }}
                                  transition={{
                                    duration: 2.5,
                                    repeat: Infinity,
                                    repeatType: 'loop'
                                  }}
                                />
                              )}
                            </div>
                          </motion.div>
                          
                          {/* Enhanced milestone content card with better visual hierarchy */}
                          <motion.div 
                            className={`
                              ml-6 p-5 rounded-xl border backdrop-blur-sm relative
                              ${isActive 
                                ? 'bg-white/95 dark:bg-zinc-800/95 border-blue-200 dark:border-blue-900/30 shadow-lg shadow-blue-500/10' 
                                : (isPassed 
                                  ? 'bg-white/80 dark:bg-zinc-800/80 border-zinc-200 dark:border-zinc-700/50 shadow-md' 
                                  : 'bg-white/60 dark:bg-zinc-800/60 border-zinc-200/80 dark:border-zinc-700/30 shadow-sm')}
                              ${isFuture ? 'opacity-75 hover:opacity-90' : 'opacity-100'}
                              transition-all duration-300 hover:shadow-md
                            `}
                            animate={isActive && !prefersReducedMotion ? {
                              y: [0, -4, 0],
                              boxShadow: [
                                '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
                                '0 10px 15px -3px rgba(59, 130, 246, 0.2)',
                                '0 4px 6px -1px rgba(59, 130, 246, 0.1)'
                              ]
                            }: { }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            whileHover={{ scale: 1.02, transition: { duration: 0.2 }}}
                          >
                            {/* Add highlight effect for active milestone */}
                            {isActive && !prefersReducedMotion && (
                              <React.Fragment>
                                <motion.div 
                                  className="absolute -inset-px rounded-xl bg-blue-500/5 z-0"
                                  animate={{ opacity: [0.5, 1, 0.5] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                />
                                <motion.div 
                                  className="absolute -left-3 top-1/2 -translate-y-1/2 w-0 h-0 
                                   border-t-[6px] border-t-transparent
                                   border-r-[6px] border-r-blue-500
                                   border-b-[6px] border-b-transparent"
                                  animate={{ x: [0, 2, 0] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                />
                              </React.Fragment>
                            )}
                            <div className="relative z-10 space-y-2">
                              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
                                {milestone.label}
                              </h3>
                              <p className="text-sm text-zinc-700 dark:text-zinc-300">
                                {milestone.subtitle}
                              </p>
                            </div>
                          </motion.div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Market Deep-Dive Questions Component - World-Class Design
const MarketDeepDiveQuestions = ({ answers, onAnswersChange }: { 
  answers: CommitmentAnswers, 
  onAnswersChange: (answers: CommitmentAnswers) => void 
}) => {
  const updateAnswer = (key: string, value: string) => {
    onAnswersChange({ ...answers, [key]: value });
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Header */}
      <motion.div 
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg">
          <Target className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-zinc-900 to-zinc-700 dark:from-zinc-100 dark:to-zinc-300 bg-clip-text text-transparent">
          Let's understand your business goals
        </h2>
        <p className="text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          While we analyze your website, help us understand your ambitions and challenges
        </p>
      </motion.div>

      <div className="space-y-10">
        {/* Question 1: Business Seriousness Scale */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  How serious are you about growing this business?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Rate your commitment level from 1 (just exploring) to 10 (all-in)</p>
              </div>
            </div>
            
            {/* Scale Selection with Premium Design */}
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-zinc-500 dark:text-zinc-400 mb-2">
                <span>Just exploring</span>
                <span>Fully committed</span>
              </div>
              <div className="flex gap-3 flex-wrap justify-center">
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <motion.button
                    key={num}
                    type="button"
                    onClick={() => updateAnswer('seriousnessScale', num.toString())}
                    className={cn(
                      "relative w-14 h-14 rounded-2xl font-bold text-lg transition-all duration-300 border-2",
                      answers.seriousnessScale === num.toString()
                        ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-500 shadow-lg scale-110"
                        : "bg-white dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 border-zinc-200 dark:border-zinc-600 hover:border-blue-300 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-zinc-600 hover:scale-105"
                    )}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {num}
                    {answers.seriousnessScale === num.toString() && (
                      <motion.div
                        className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", duration: 0.3 }}
                      >
                        <CheckCircle2 className="h-3 w-3 text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Question 2: Biggest Challenge */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What's your biggest user acquisition challenge?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Understanding your main challenge helps us prioritize the right strategies</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { option: 'Getting traffic', icon: TrendingUp, color: 'from-blue-500 to-blue-600' },
                { option: 'Converting visitors', icon: Target, color: 'from-emerald-500 to-emerald-600' },
                { option: 'Creating content', icon: PenTool, color: 'from-blue-500 to-blue-600' },
                { option: 'Finding customers', icon: Users, color: 'from-blue-500 to-blue-600' },
                { option: 'Building awareness', icon: Megaphone, color: 'from-pink-500 to-pink-600' },
                { option: 'Other', icon: MoreHorizontal, color: 'from-zinc-500 to-zinc-600' }
              ].map(({ option, icon: Icon, color }) => (
                <motion.button
                  key={option}
                  type="button"
                  onClick={() => updateAnswer('biggestChallenge', option)}
                  className={cn(
                    "relative p-6 rounded-2xl border-2 transition-all duration-300 text-left group",
                    answers.biggestChallenge === option
                      ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg"
                      : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                  )}
                  whileHover={{ y: -2, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                      color,
                      answers.biggestChallenge === option ? "shadow-lg scale-110" : "opacity-70 group-hover:opacity-100"
                    )}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-zinc-900 dark:text-white text-lg">{option}</h4>
                    </div>
                    {answers.biggestChallenge === option && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", duration: 0.3 }}
                        className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center"
                      >
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      </motion.div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Question 3: Revenue Goal */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What's your target revenue goal in 6 months?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">This helps us recommend the right growth strategies for your stage</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { goal: '$1k/month', stage: 'First Revenue', color: 'from-zinc-400 to-zinc-500' },
                { goal: '$2-4k/month', stage: 'Early Growth', color: 'from-blue-400 to-blue-500' },
                { goal: '$5-7k/month', stage: 'Building Momentum', color: 'from-blue-400 to-blue-500' },
                { goal: '$7-10k/month', stage: 'Solid Foundation', color: 'from-blue-400 to-blue-500' },
                { goal: '$10-20k/month', stage: 'Scaling Up', color: 'from-emerald-400 to-emerald-500' },
                { goal: '$20k+/month', stage: 'High Growth', color: 'from-amber-400 to-amber-500' }
              ].map(({ goal, stage, color }) => (
                <motion.button
                  key={goal}
                  type="button"
                  onClick={() => updateAnswer('revenueGoal', goal)}
                  className={cn(
                    "relative p-6 rounded-2xl border-2 transition-all duration-300 text-center",
                    answers.revenueGoal === goal
                      ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg"
                      : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                  )}
                  whileHover={{ y: -2, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={cn(
                    "w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                    color,
                    answers.revenueGoal === goal ? "shadow-lg scale-110" : "opacity-70"
                  )}>
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-zinc-900 dark:text-white text-lg mb-1">{goal}</h4>
                  <p className="text-sm text-zinc-600 dark:text-zinc-300">{stage}</p>
                  {answers.revenueGoal === goal && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.3 }}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center"
                    >
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Question 4: Time Commitment */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  How much time can you dedicate to marketing weekly?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">This helps us recommend realistic strategies that fit your schedule</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { time: '1-2 hours', level: 'Light', desc: 'Weekends only', color: 'from-amber-400 to-amber-500' },
                { time: '3-5 hours', level: 'Moderate', desc: 'Few evenings', color: 'from-blue-400 to-blue-500' },
                { time: '6-10 hours', level: 'Dedicated', desc: 'Daily effort', color: 'from-blue-400 to-blue-500' },
                { time: '10+ hours', level: 'Intensive', desc: 'Marketing focus', color: 'from-emerald-400 to-emerald-500' }
              ].map(({ time, level, desc, color }) => (
                <motion.button
                  key={time}
                  type="button"
                  onClick={() => updateAnswer('timeCommitment', time)}
                  className={cn(
                    "relative p-6 rounded-2xl border-2 transition-all duration-300 text-center",
                    answers.timeCommitment === time
                      ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg"
                      : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                  )}
                  whileHover={{ y: -2, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={cn(
                    "w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                    color,
                    answers.timeCommitment === time ? "shadow-lg scale-110" : "opacity-70"
                  )}>
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-zinc-900 dark:text-white text-lg mb-1">{time}</h4>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mb-1">{level}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">{desc}</p>
                  {answers.timeCommitment === time && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.3 }}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center"
                    >
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Audience Insights Questions Component - World-Class Design
const AudienceInsightsQuestions = ({ answers, onAnswersChange }: { 
  answers: any, 
  onAnswersChange: (answers: any) => void 
}) => {
  const updateAnswer = (key: string, value: string) => {
    onAnswersChange({ ...answers, [key]: value });
  };

  const platforms = [
    { name: 'LinkedIn', icon: Users, color: 'from-blue-500 to-blue-600' },
    { name: 'Twitter/X', icon: MessageSquare, color: 'from-zinc-500 to-zinc-600' },
    { name: 'Reddit', icon: MessagesSquare, color: 'from-orange-500 to-red-600' },
    { name: 'Facebook', icon: Users2, color: 'from-blue-600 to-blue-700' },
    { name: 'YouTube', icon: Video, color: 'from-red-500 to-red-600' },
    { name: 'Instagram', icon: Camera, color: 'from-pink-500 to-blue-600' },
    { name: 'Discord', icon: MessageCircle, color: 'from-blue-500 to-blue-600' },
    { name: 'Slack', icon: MessageSquare, color: 'from-blue-500 to-blue-600' },
    { name: 'Forums', icon: MessagesSquare, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Other', icon: MoreHorizontal, color: 'from-zinc-500 to-zinc-600' }
  ];

  const channels = [
    { name: 'SEO/Content', icon: Search, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Paid ads', icon: Target, color: 'from-blue-500 to-blue-600' },
    { name: 'Social media', icon: Share2, color: 'from-blue-500 to-blue-600' },
    { name: 'Email marketing', icon: MailOpen, color: 'from-blue-500 to-blue-600' },
    { name: 'Cold outreach', icon: UserPlus, color: 'from-amber-500 to-orange-600' },
    { name: 'Partnerships', icon: Handshake, color: 'from-emerald-500 to-teal-600' },
    { name: 'Events', icon: CalendarDays, color: 'from-pink-500 to-pink-600' },
    { name: 'None yet', icon: CircleX, color: 'from-zinc-500 to-zinc-600' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Header */}
      <motion.div 
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg">
          <Users className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-zinc-900 to-zinc-700 dark:from-zinc-100 dark:to-zinc-300 bg-clip-text text-transparent">
          Tell us about your target customers
        </h2>
        <p className="text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          Understanding your audience is key to effective marketing strategies
        </p>
      </motion.div>

      <div className="space-y-10">
        {/* Question 1: Ideal Customer Description */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  Describe your ideal customer in 3 words
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Help us understand who you're targeting with concise descriptors</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <Input
                placeholder="e.g., busy, tech-savvy, professionals"
                value={answers.idealCustomer || ''}
                onChange={(e) => updateAnswer('idealCustomer', e.target.value)}
                className="h-14 text-lg bg-zinc-50 dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 focus:bg-white dark:focus:bg-zinc-700 focus:border-blue-300 dark:focus:border-blue-500 transition-all duration-200 dark:text-white"
              />
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Think about demographics, behavior, or professional characteristics</p>
            </div>
          </div>
        </motion.div>

        {/* Question 2: Main Problem */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What's the main problem your product solves?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Clearly articulating the problem helps us craft compelling messaging</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <Textarea
                placeholder="Describe the core problem your business addresses..."
                value={answers.mainProblem || ''}
                onChange={(e) => updateAnswer('mainProblem', e.target.value)}
                className="h-32 text-lg bg-zinc-50 dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 focus:bg-white dark:focus:bg-zinc-700 focus:border-blue-300 dark:focus:border-blue-500 transition-all duration-200 resize-none dark:text-white"
              />
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Focus on the pain point your customers experience before finding your solution</p>
            </div>
          </div>
        </motion.div>

        {/* Question 3: Where Customers Hang Out */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  Where do your customers currently hang out online?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Select all platforms where we can find your target audience (multiple selections allowed)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {platforms.map(({ name, icon: Icon, color }) => {
                const isSelected = (answers.customerHangout || '').split(',').filter(Boolean).includes(name);
                return (
                  <motion.button
                    key={name}
                    type="button"
                    onClick={() => {
                      const current = (answers.customerHangout || '').split(',').filter(Boolean);
                      if (current.includes(name)) {
                        updateAnswer('customerHangout', current.filter(c => c !== name).join(','));
                      } else {
                        updateAnswer('customerHangout', [...current, name].join(','));
                      }
                    }}
                    className={cn(
                      "relative p-4 rounded-2xl border-2 transition-all duration-300 text-center",
                      isSelected
                        ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg scale-105"
                        : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                    )}
                    whileHover={{ y: -2, boxShadow: "0 10px 20px -5px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={cn(
                      "w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                      color,
                      isSelected ? "shadow-md scale-110" : "opacity-70"
                    )}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-medium text-zinc-900 dark:text-white text-sm">{name}</h4>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", duration: 0.3 }}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center"
                      >
                        <CheckCircle2 className="h-3 w-3 text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Question 4: Marketing Channels Tried */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What marketing channels have you tried before?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Help us understand your marketing experience (multiple selections allowed)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {channels.map(({ name, icon: Icon, color }) => {
                const isSelected = (answers.marketingChannels || '').split(',').filter(Boolean).includes(name);
                return (
                  <motion.button
                    key={name}
                    type="button"
                    onClick={() => {
                      const current = (answers.marketingChannels || '').split(',').filter(Boolean);
                      if (current.includes(name)) {
                        updateAnswer('marketingChannels', current.filter(c => c !== name).join(','));
                      } else {
                        updateAnswer('marketingChannels', [...current, name].join(','));
                      }
                    }}
                    className={cn(
                      "relative p-6 rounded-2xl border-2 transition-all duration-300 text-center",
                      isSelected
                        ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg scale-105"
                        : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                    )}
                    whileHover={{ y: -2, boxShadow: "0 10px 20px -5px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={cn(
                      "w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                      color,
                      isSelected ? "shadow-lg scale-110" : "opacity-70"
                    )}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-zinc-900 dark:text-white text-base mb-1">{name}</h4>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", duration: 0.3 }}
                        className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center"
                      >
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>
             </div>
     </div>
   );
};



// Content Strategy Questions Component - World-Class Design
const ContentStrategyQuestions = ({ answers, onAnswersChange }: { 
  answers: CommitmentAnswers, 
  onAnswersChange: (answers: CommitmentAnswers) => void 
}) => {
  const updateAnswer = (key: string, value: string) => {
    onAnswersChange({ ...answers, [key]: value });
  };

  const discoveryMethods = [
    { name: 'Google search', icon: Search, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Social media', icon: Share2, color: 'from-blue-500 to-blue-600' },
    { name: 'Word of mouth', icon: Users, color: 'from-blue-500 to-blue-600' },
    { name: 'Content marketing', icon: FileText, color: 'from-blue-500 to-blue-600' },
    { name: 'Paid advertising', icon: Target, color: 'from-amber-500 to-orange-600' },
    { name: 'Partnerships', icon: Handshake, color: 'from-emerald-500 to-teal-600' }
  ];

  const contentFormats = [
    { name: 'Blog posts', icon: FileText, color: 'from-blue-500 to-blue-600' },
    { name: 'Videos', icon: Video, color: 'from-red-500 to-red-600' },
    { name: 'Podcasts', icon: Mic, color: 'from-blue-500 to-blue-600' },
    { name: 'Social posts', icon: MessageSquare, color: 'from-blue-500 to-blue-600' },
    { name: 'Email newsletters', icon: Mail, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Case studies', icon: BarChart3, color: 'from-amber-500 to-orange-600' },
    { name: 'Guides/Tutorials', icon: BookOpen, color: 'from-teal-500 to-teal-600' },
    { name: 'Webinars', icon: MonitorPlay, color: 'from-pink-500 to-pink-600' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Header */}
      <motion.div 
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg">
          <PenTool className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-zinc-900 to-zinc-700 dark:from-zinc-100 dark:to-zinc-300 bg-clip-text text-transparent">
          Define your marketing and content approach
        </h2>
        <p className="text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          Help us understand how you want to be discovered and known in your market
        </p>
      </motion.div>

      <div className="space-y-10">
        {/* Question 1: Brand Topics */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What topics should your brand be known for?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">List the key topics that define your expertise and industry authority</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <Textarea
                placeholder="e.g., productivity, automation, business growth, team collaboration..."
                value={answers.brandTopics || ''}
                onChange={(e) => updateAnswer('brandTopics', e.target.value)}
                className="h-32 text-lg bg-zinc-50 dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 focus:bg-white dark:focus:bg-zinc-700 focus:border-blue-300 dark:focus:border-blue-500 transition-all duration-200 resize-none dark:text-white"
              />
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Think about the problems you solve and the expertise you want to be recognized for</p>
            </div>
          </div>
        </motion.div>

        {/* Question 2: Discovery Methods */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  How do you want customers to discover you?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Select all discovery channels that align with your growth strategy (multiple selections allowed)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {discoveryMethods.map(({ name, icon: Icon, color }) => {
                const isSelected = (answers.discoveryMethod || '').split(',').filter(Boolean).includes(name);
                return (
                  <motion.button
                    key={name}
                    type="button"
                    onClick={() => {
                      const current = (answers.discoveryMethod || '').split(',').filter(Boolean);
                      if (current.includes(name)) {
                        updateAnswer('discoveryMethod', current.filter(c => c !== name).join(','));
                      } else {
                        updateAnswer('discoveryMethod', [...current, name].join(','));
                      }
                    }}
                    className={cn(
                      "relative p-6 rounded-2xl border-2 transition-all duration-300 text-center",
                      isSelected
                        ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg scale-105"
                        : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                    )}
                    whileHover={{ y: -2, boxShadow: "0 10px 20px -5px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={cn(
                      "w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                      color,
                      isSelected ? "shadow-lg scale-110" : "opacity-70"
                    )}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-zinc-900 dark:text-white text-base mb-1">{name}</h4>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Question 3: Content Formats */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What content formats work best for your audience?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">Choose the content types that resonate with your target customers (multiple selections allowed)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {contentFormats.map(({ name, icon: Icon, color }) => {
                const isSelected = (answers.contentFormats || '').split(',').filter(Boolean).includes(name);
                return (
                  <motion.button
                    key={name}
                    type="button"
                    onClick={() => {
                      const current = (answers.contentFormats || '').split(',').filter(Boolean);
                      if (current.includes(name)) {
                        updateAnswer('contentFormats', current.filter(c => c !== name).join(','));
                      } else {
                        updateAnswer('contentFormats', [...current, name].join(','));
                      }
                    }}
                    className={cn(
                      "relative p-6 rounded-2xl border-2 transition-all duration-300 text-center",
                      isSelected
                        ? "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/30 border-blue-300 dark:border-blue-600 shadow-lg scale-105"
                        : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-blue-200 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-zinc-600"
                    )}
                    whileHover={{ y: -2, boxShadow: "0 10px 20px -5px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={cn(
                      "w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br flex items-center justify-center transition-all duration-300",
                      color,
                      isSelected ? "shadow-lg scale-110" : "opacity-70"
                    )}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-zinc-900 dark:text-white text-base mb-1">{name}</h4>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Question 4: Keywords */}
        <motion.div 
          className="group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-white dark:bg-zinc-800 rounded-3xl p-8 shadow-xl border border-zinc-100 dark:border-zinc-700 transition-all duration-300 group-hover:shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  What keywords do you want to rank for?
                </h3>
                <p className="text-zinc-600 dark:text-zinc-300">List the search terms your ideal customers use when looking for solutions like yours</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <Input
                placeholder="e.g., project management, team collaboration, productivity tools, workflow automation"
                value={answers.targetKeywords || ''}
                onChange={(e) => updateAnswer('targetKeywords', e.target.value)}
                className="h-14 text-lg bg-zinc-50 dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 focus:bg-white dark:focus:bg-zinc-700 focus:border-blue-300 dark:focus:border-blue-500 transition-all duration-200 dark:text-white"
              />
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Think about the terms your customers search for when they have the problem you solve</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export function OnboardingWizard({ onComplete, isLoading, onStepChange }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [insightsCompleted, setInsightsCompleted] = useState(false);
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Dynamic title state for analysis results
  const [currentAnalysisPageTitle, setCurrentAnalysisPageTitle] = useState("Marketing Insights");
  
  // Commitment-building questions state
  const [commitmentAnswers, setCommitmentAnswers] = useState({
    // Market Deep-Dive
    seriousnessScale: '',
    biggestChallenge: '',
    revenueGoal: '',
    timeCommitment: '',
    
    // Audience Insights
    idealCustomer: '',
    mainProblem: '',
    customerHangout: '',
    marketingChannels: '',
    
    // Content Strategy
    brandTopics: '',
    discoveryMethod: '',
    contentFormats: '',
    targetKeywords: ''
  });

  const navigate = useNavigate();
  const { toast } = useToast();

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Sign in required",
          description: "Please sign in to subscribe.",
          variant: "destructive",
        });
        setCheckoutLoading(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://ngfwjlgmdcoxewihfmlt.supabase.co";
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          planId: "pro",
          billingPeriod: "monthly",
          currency: getUserCurrency().toLowerCase(),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create checkout session");
      }
      if (!data?.url) {
        throw new Error("No checkout URL received");
      }
      window.location.href = data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Checkout error",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  // Ensure the intro animation shows on initial mount
  useEffect(() => {
    // Force the intro animation to show
    setShowIntro(true);
  }, []);
  
  // Notify parent when step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);
  
  // ✅ CRITICAL FIX: Timeout mechanism to prevent infinite loading
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    
    if (analysisLoading && !analysisComplete) {
      // Start 3-minute timeout when analysis starts loading
      timeoutId = setTimeout(() => {
        console.log("Analysis timeout - marking as complete with error");
        setAnalysisLoading(false);
        setAnalysisComplete(true);
        setAnalysisError("Analysis took too long. Please try again.");
        toast({
          title: "Analysis Timeout",
          description: "The analysis is taking longer than expected. You can continue with manual setup.",
          variant: "destructive",
        });
      }, 180000); // 3 minutes timeout
    }
    
    // Cleanup timeout if analysis completes or component unmounts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [analysisLoading, analysisComplete, toast]);
  
  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      businessName: "",
      companyType: "",
      industry: "",
    },
  });
  
  const steps = [
    {
      title: "Welcome to LaunchGuide",
      description: "Let's get your business details set up",
      icon: Building2,
      fields: ["businessName", "companyType", "industry"],
    },
    {
      title: "Website Analysis",
      description: "Let's analyze your website to understand your product",
      icon: Globe,
      fields: [],
    },
    {
      title: "Market Deep-Dive",
      description: "Tell us about your business goals and challenges",
      icon: Target,
      fields: [],
    },
    {
      title: "Audience Insights", 
      description: "Help us understand your target customers",
      icon: Users,
      fields: [],
    },
    {
      title: "Content Strategy",
      description: "Define your marketing and content approach", 
      icon: PenTool,
      fields: [],
    },
    {
      title: currentAnalysisPageTitle,
      description: "Here's what we've learned about your business",
      icon: BarChart4,
      fields: [],
    },
  ];
  
  const totalSteps = steps.length;
  
  const nextStep = () => {
    // Helper function to scroll to top after state update
    const scrollToTopAfterUpdate = () => {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100); // Small delay to ensure DOM has updated
    };

    if (currentStep === 0) {
      // Validate company details
      form.trigger(["businessName", "companyType", "industry"]).then(async (isValid) => {
        if (isValid) {
          // Create the project immediately after step 1
          try {
            setIsLoadingCreate(true);

            // Get current user
            const { data: { user }} = await supabase.auth.getUser();
            if (!user) {
              throw new Error("User not authenticated");
            }

            // Format X handle: add @ if needed or set to undefined if empty
            const formValues = form.getValues();
            let xHandle = undefined;
            if (formValues.xHandle && formValues.xHandle.trim()) {
              xHandle = formValues.xHandle.trim();
              // Add @ if missing
              if (!xHandle.startsWith('@')) {
                xHandle = `@${xHandle}`;
              }
            }

            // Create project with business details in a single operation
            const { data: project, error }= await supabase
              .from("projects")
              .insert([{ 
                name: formValues.businessName,
                business_name: formValues.businessName,
                company_type: formValues.companyType,
                industry: formValues.industry,
                user_id: user.id,
                created_at: new Date().toISOString(),
                x_handle: xHandle, // Now we can include this directly
              }])
              .select()
              .single();

            if (error) {
              console.error("Supabase error details:", error);
              throw error;
            }

            // Store project ID for later use with website analysis
            setProjectId(project.id);
            
            toast({
              title: "Project created",
              description: "Your project has been created. Now let's analyze your website!",
            });

            // Move to next step and scroll to top
            setCurrentStep(1);
            scrollToTopAfterUpdate();
          }catch (error) {
            console.error("Error creating project:", error);
            toast({
              title: "Error",
              description: "Failed to create your project. Please try again.",
              variant: "destructive",
            });
          }finally {
            setIsLoadingCreate(false);
          }
        }
      });
    }else if (currentStep === 1) {
      // Move to Market Deep-Dive questions immediately after URL input - analysis runs in background
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      scrollToTopAfterUpdate();
    }else if (currentStep >= 2 && currentStep <= 4) {
      // Handle question steps (Market Deep-Dive, Audience Insights, Content Strategy)
      if (currentStep === 4) {
        // Moving from final questions to results - check if analysis is complete
        if (!analysisComplete || analysisLoading) {
          // Show a much better UX with visual feedback
          toast({
            title: "⏳ Analysis in progress",
            description: "Your website analysis is still running. We'll show you a progress indicator...",
            variant: "default",
          });
          
          // Proceed to a special "waiting for analysis" step
          const newStep = currentStep + 1;
          setCurrentStep(newStep);
          scrollToTopAfterUpdate();
          return;
        }
      }
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      scrollToTopAfterUpdate();
    }else if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      scrollToTopAfterUpdate();
      
      // If it's the final "all set" step, show confetti
      if (newStep === totalSteps - 1) {
        setShowConfetti(true);
        setTimeout(() => {
          toast({
            title: "Success!",
            description: "Your project has been created successfully.",
          });
        }, 500);
      }
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      if (onStepChange) onStepChange(newStep);
      
      // Scroll to top after navigating back
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };
  
  const handleAnalysisSuccess = async (results: any) => {
    try {
      // Always fetch the latest results from the database
      if (!projectId) {
        console.error("Cannot fetch results: No project ID available");
        toast({
          title: "Analysis Issue",
          description: "Could not fetch analysis results - missing project ID",
          variant: "destructive",
        });
        setAnalysisLoading(false);
        setAnalysisError("Missing project ID. Please try again.");
        return;
      }
      
      console.log("Fetching latest results from database for project:", projectId);
      const { data: crawl, error }= await supabase
        .from('website_crawls')
        .select('analysis_results')
        .eq('project_id', projectId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (error) {
        console.error("Error fetching analysis results from database:", error);
        toast({
          title: "Analysis Issue",
          description: "Error retrieving analysis results. Please try again.",
          variant: "destructive",
        });
        setAnalysisLoading(false);
        setAnalysisError("Error retrieving analysis results. Please try again.");
        return;
      }
      
      if (!crawl?.analysis_results) {
        console.error("No analysis results found in database");
        toast({
          title: "Analysis Issue",
          description: "No analysis results found. Please try again.",
          variant: "destructive",
        });
        setAnalysisLoading(false);
        setAnalysisComplete(true); // ✅ CRITICAL FIX: Mark analysis as complete even if no results
        setAnalysisError("No analysis results found. Please try again.");
        return;
      }
      
      // Use the results from the database
      results = crawl.analysis_results;
      console.log("Successfully fetched analysis results from database:", results);
      
      // CRITICAL: Set state variables immediately
      setAnalysisResults(results);
      setAnalysisComplete(true);
      setAnalysisError(null);
      setAnalysisLoading(false);
      
      // Ensure there's a slight delay before navigation so state updates can complete
      setTimeout(() => {
        console.log("Analysis complete, results available for step progression");
        // DON'T force step change - let user continue naturally where they are
        // Only suggest progression if user is still on analysis step (step 1)
        if (currentStep === 1) {
          console.log("User still on analysis step, enabling progression to questions");
          // Don't auto-navigate - just enable the Continue button
        }
      }, 500);
      
    }catch (error) {
      console.error("Error processing analysis results:", error);
      setAnalysisError("Error processing analysis results. Please try again.");
      setAnalysisLoading(false);
      setAnalysisComplete(true); // ✅ CRITICAL FIX: Mark analysis as complete even if error
      toast({
        title: "Analysis Processing Error",
        description: "There was a problem processing the analysis results. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAnalysisStart = (url?: string) => {
    // Reset any previous errors and set loading state
    setAnalysisError(null);
    setAnalysisLoading(true);
    setAnalysisComplete(false);
    
    // If we have a URL, store it for potential later use
    if (url) {
      // Store the URL in state if needed for display purposes
      // This could be useful if we need to show it in the UI later
    }
    
    // CRITICAL: Immediately proceed to Market Deep-Dive questions after analysis starts
    // Analysis continues in background while user answers commitment-building questions
    nextStep();
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    nextStep(); // Move to the research insights step
  };
  
  const handleSubmit = async (values: ProjectFormValues) => {
    // If we've already created the project, pass the ID back
    if (projectId) {
      onComplete({
        ...values,
        projectId
      }as any); // Use type assertion to allow additional properties
      
      setShowPaywall(true);
      return;
    }
    
    // CRITICAL FIX: If no projectId, create project now as fallback
    console.warn("No projectId found during onboarding completion. Creating project now as fallback.");
    
    try {
      setIsLoadingCreate(true);
      
      // Get current user
      const { data: { user }} = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Create project with business details as fallback
      const { data: project, error } = await supabase
        .from("projects")
        .insert([{ 
          name: values.businessName,
          business_name: values.businessName,
          company_type: values.companyType,
          industry: values.industry,
          user_id: user.id,
          created_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) {
        console.error("Fallback project creation error:", error);
        throw error;
      }

      // Set the project ID and complete onboarding
      setProjectId(project.id);
      
      onComplete({
        ...values,
        projectId: project.id
      }as any);
      
      setShowPaywall(true);
      
    } catch (error) {
      console.error("Fallback project creation failed:", error);
      toast({
        title: "Error",
        description: "Failed to create your project. Please contact support.",
        variant: "destructive",
      });
      // As last resort, navigate to projects list
      navigate('/projects');
    } finally {
      setIsLoadingCreate(false);
    }
  };
  
  return (
    <div className="max-w-full w-full mx-auto">
      <AnimatePresence mode="wait">
        {showIntro && (
          <IntroAnimation onComplete={() => setShowIntro(false)}/>
        )}
      </AnimatePresence>

      {showConfetti && (
        <Confetti
          particleCount={200}
          spread={180}
          duration={3000}
          onComplete={() => setShowConfetti(false)}
        />
      )}

      {showPaywall ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="w-full max-w-2xl mx-auto"
        >
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="p-8 md:p-10 lg:p-12">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-2">
                  Unlock LaunchGuide Pro to get your roadmap to $10K MRR
                </h2>
                <p className="text-lg text-muted-foreground">
                  €29/month. Playbooks, analysis, and every AI tool.
                </p>
              </div>

              <div className="mb-10 md:mb-12 overflow-hidden rounded-xl border border-border">
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left font-medium text-muted-foreground py-3 px-4 w-1/2 min-w-0">
                        Without LaunchGuide
                      </th>
                      <th className="text-left font-medium text-foreground py-3 px-4 w-1/2 min-w-0">
                        With LaunchGuide
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="py-3 px-4 text-muted-foreground min-w-0 break-words">Figuring everything out yourself</td>
                      <td className="py-3 px-4 text-foreground min-w-0 break-words">Step-by-step roadmap that works</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-muted-foreground min-w-0 break-words">Guessing your audience, or marketing to no one in particular</td>
                      <td className="py-3 px-4 text-foreground min-w-0 break-words">We find your audience, market, and users&apos; pain points</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-muted-foreground min-w-0 break-words">Generic advice from blogs and courses</td>
                      <td className="py-3 px-4 text-foreground min-w-0 break-words">AI analysis tailored to your site and market</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-muted-foreground min-w-0 break-words">Scattered tools and guesswork</td>
                      <td className="py-3 px-4 text-foreground min-w-0 break-words">One system: playbooks, research, content</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-muted-foreground min-w-0 break-words">Building launch strategy from scratch</td>
                      <td className="py-3 px-4 text-foreground min-w-0 break-words">Product Hunt, Reddit, launch guides included</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10 md:mb-12">
                <blockquote className="flex flex-col rounded-xl border border-border bg-muted/30 px-5 py-5 md:px-6 md:py-6">
                  <p className="text-foreground text-[15px] md:text-base leading-[1.6] mb-4">
                    &ldquo;The playbooks give you a clear roadmap instead of trying to figure everything out yourself. Finally, a step-by-step system that actually works.&rdquo;
                  </p>
                  <footer className="mt-auto text-sm text-muted-foreground">
                    LaunchGuide user
                  </footer>
                </blockquote>
                <blockquote className="flex flex-col rounded-xl border border-border bg-muted/30 px-5 py-5 md:px-6 md:py-6">
                  <p className="text-foreground text-[15px] md:text-base leading-[1.6] mb-4">
                    &ldquo;LaunchGuide has transformed how I market our products. It&apos;s like having an entire marketing team at your fingertips.&rdquo;
                  </p>
                  <footer className="mt-auto text-sm text-muted-foreground">
                    LaunchGuide user
                  </footer>
                </blockquote>
              </div>

              <div className="mb-10 md:mb-12">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  What you get
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  With Pro you get a clear roadmap and every tool to execute it. No more guessing.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-foreground">
                  <li>Step-by-step playbooks to reach $10K MRR</li>
                  <li>Website analysis and market insights</li>
                  <li>AI content ideas and topic research</li>
                  <li>Competitor and audience research tools</li>
                  <li>Marketing vault: USPs, bios, offer scripts</li>
                  <li>Product Hunt, Reddit, and launch guides</li>
                  <li>Ongoing updates and new tools</li>
                </ul>
              </div>

              <div className="flex flex-col items-center gap-4">
                <Button
                  size="lg"
                  className="w-full max-w-sm bg-primary hover:bg-primary/90 text-primary-foreground text-base font-medium py-6 rounded-lg"
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="size-4 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin" />
                      Preparing checkout...
                    </span>
                  ) : (
                    "Unlock LaunchGuide (€29/month)"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Secure checkout · Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <>
      <div className="mb-6 md:mb-8">
        <Progress 
          value={((currentStep + 1) / totalSteps) * 100} 
          className="h-2 bg-muted" 
        />
      </div>
      
      <div className="flex gap-2 md:gap-4 mb-6 md:mb-8 px-0 md:px-4">
        {steps.map((step, index) => (
          <div key={index} className="flex-1 min-w-0">
            <div 
              className={cn(
                "flex flex-col items-center justify-center text-center transition-colors duration-200",
                index < currentStep 
                  ? "text-primary" 
                  : index === currentStep 
                    ? "text-primary" 
                    : "text-muted-foreground"
              )}
            >
              <div 
                className={cn(
                  "size-10 md:size-12 rounded-full flex items-center justify-center mb-1.5 md:mb-2 shrink-0",
                  index < currentStep 
                    ? "bg-primary/10 text-primary" 
                    : index === currentStep 
                      ? "bg-primary/15 ring-2 ring-primary/30 text-primary" 
                      : "bg-muted text-muted-foreground"
                )}
              >
                {index < currentStep ? (
                  <CheckCircle2 className="size-5 md:size-6" />
                ) : (
                  <step.icon className="size-5 md:size-6" />
                )}
              </div>
              <span className={cn(
                "text-[10px] md:text-xs font-medium truncate w-full px-0.5",
                index < currentStep 
                  ? "text-primary" 
                  : index === currentStep 
                    ? "text-primary" 
                    : "text-muted-foreground"
              )}>
                {step.title}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      <Card className="w-full border border-border shadow-sm rounded-xl overflow-hidden">
        <CardHeader className="text-center pb-4 md:pb-6">
          {currentStep !== 0 && currentStep !== 1 && currentStep !== 2 && (
            <>
              <CardTitle className="text-xl md:text-2xl font-bold">
                {steps[currentStep].title}
              </CardTitle>
              <CardDescription className="text-base md:text-lg text-muted-foreground mt-1">
                {steps[currentStep].description}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <CardContent className="space-y-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`step-content-${currentStep}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="min-h-[300px]"
                >
                  {currentStep === 0 && (
                    <div className="space-y-8 relative">
                      {/* Replace static background with mouse-reactive background */}
                      <MouseReactiveBackground />

                      {/* Hero section */}
                      <div className="mb-8 text-center relative">
                        <motion.div 
                          className="relative mx-auto"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8 }}
                        >
                          <div className="relative w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                            {/* 3D document icon with rocket */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 rounded-xl shadow-lg transform -rotate-6 scale-95"></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/50 dark:to-blue-900/50 rounded-xl shadow-lg transform rotate-3 scale-105"></div>
                            <div className="relative w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-xl flex items-center justify-center">
                              <motion.div
                                animate={{ y: [0, -2, 0] }}
                                transition={{ 
                                  duration: 2, 
                                  repeat: Infinity,
                                  repeatType: "reverse",
                                  ease: "easeInOut" 
                                }}
                              >
                                <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 12H18M8 7H15M8 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <motion.path 
                                    d="M19 5L22 2L19 5ZM19 5L16 2L19 5ZM19 5V11" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: [0, 1, 0] }}
                                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                                  />
                                </svg>
                              </motion.div>
                            </div>
                            
                            {/* Animated particles around the icon */}
                            {[...Array(5)].map((_, i) => (
                              <motion.div
                                key={`hero-particle-${i}`}
                                className="absolute rounded-full bg-blue-500"
                                style={{
                                  width: 4,
                                  height: 4,
                                  left: '50%',
                                  top: '50%',
                                }}
                                initial={{ x: 0, y: 0, opacity: 0 }}
                                animate={{
                                  x: [0, (Math.random() * 30 - 15)],
                                  y: [0, (Math.random() * -40 - 10)],
                                  opacity: [0, 0.8, 0],
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  repeatDelay: Math.random() * 2,
                                  ease: "easeOut",
                                }}
                              />
                            ))}
                          </div>
                          
                          <motion.h2 
                            className="text-2xl sm:text-3xl font-bold gradient-text mb-2"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                          >
                            Welcome to LaunchGuide
                          </motion.h2>
                          <motion.p
                            className="text-muted-foreground text-base md:text-lg"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                          >
                            Let's get your business details set up
                          </motion.p>
                        </motion.div>
                      </div>

                      <motion.div
                        className="space-y-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                      >
                      <FormField
                        control={form.control}
                        name="businessName"
                        render={({ field }) => (
                            <FormItem className="relative">
                              <FormLabel className="text-sm font-medium text-zinc-700 dark:text-zinc-200 ml-1">Business Name</FormLabel>
                            <FormControl>
                                <div className="relative group">
                                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-0 group-focus-within:opacity-30 transition-opacity blur"></div>
                              <Input 
                                placeholder="Enter your business name..." 
                                    className="h-14 pl-4 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-800 bg-white dark:bg-zinc-800 rounded-xl transition-all duration-300 relative z-10" 
                                {...field}
                              />
                                  <motion.div 
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 z-10 pointer-events-none"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ 
                                      opacity: field.value ? 1 : 0, 
                                      scale: field.value ? 1 : 0 
                                    }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                  >
                                    {field.value && <CheckCircle2 className="h-5 w-5" />}
                                  </motion.div>
                                </div>
                            </FormControl>
                              <FormMessage className="ml-1" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="companyType"
                        render={({ field }) => (
                            <FormItem className="relative">
                              <FormLabel className="text-sm font-medium text-zinc-700 dark:text-zinc-200 ml-1">Company Type</FormLabel>
                            <Select onValueChange={field.onChange}defaultValue={field.value}>
                              <FormControl>
                                  <div className="relative group">
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-0 group-focus-within:opacity-30 transition-opacity blur"></div>
                                    <SelectTrigger className="h-14 pl-4 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-800 bg-white dark:bg-zinc-800 rounded-xl transition-all duration-300 relative z-10">
                                  <SelectValue placeholder="Select company type" />
                                </SelectTrigger>
                                  </div>
                              </FormControl>
                                <SelectContent className="border-zinc-200 dark:border-zinc-700">
                                {companyTypes.map((type) => (
                                    <SelectItem 
                                      key={type.id}
                                      value={type.id}
                                      className="flex items-center py-3 px-2 cursor-pointer data-[highlighted]:text-zinc-900 dark:data-[highlighted]:text-zinc-100 data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-900/20"
                                    >
                                      <div className="flex items-center">
                                        {type.id === 'b2b' ? (
                                          <Building2 className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                                        ) : (
                                          <Users2 className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                                        )}
                                    {type.name}
                                      </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                              <FormMessage className="ml-1" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="industry"
                        render={({ field }) => (
                            <FormItem className="relative">
                              <FormLabel className="text-sm font-medium text-zinc-700 dark:text-zinc-200 ml-1">Industry</FormLabel>
                            <Select onValueChange={field.onChange}defaultValue={field.value}>
                              <FormControl>
                                  <div className="relative group">
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-0 group-focus-within:opacity-30 transition-opacity blur"></div>
                                    <SelectTrigger className="h-14 pl-4 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-800 bg-white dark:bg-zinc-800 rounded-xl transition-all duration-300 relative z-10">
                                  <SelectValue placeholder="Select industry" />
                                </SelectTrigger>
                                  </div>
                              </FormControl>
                                <SelectContent className="border-zinc-200 dark:border-zinc-700 max-h-[300px]">
                                  {industries.map((industry) => {
                                    // Map industries to appropriate icons
                                    let IndustryIcon = Lightbulb; // Default icon
                                    
                                    // Assign specific icons based on industry type
                                    if (industry === "Technology") IndustryIcon = Cpu;
                                    else if (industry === "Finance") IndustryIcon = BarChart3;
                                    else if (industry === "Marketing") IndustryIcon = Target;
                                    else if (industry === "Education") IndustryIcon = Lightbulb;
                                    
                                    return (
                                      <SelectItem 
                                        key={industry}
                                        value={industry}
                                        className="flex items-center py-3 px-2 cursor-pointer data-[highlighted]:text-zinc-900 dark:data-[highlighted]:text-zinc-100 data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-900/20"
                                      >
                                        <div className="flex items-center">
                                          <IndustryIcon className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    {industry}
                                        </div>
                                  </SelectItem>
                                    );
                                  })}
                              </SelectContent>
                            </Select>
                              <FormMessage className="ml-1" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="xHandle"
                        render={({ field }) => (
                            <FormItem className="relative">
                              <FormLabel className="text-sm font-medium text-zinc-700 dark:text-zinc-200 ml-1">X.com handle (Optional)</FormLabel>
                            <FormControl>
                                <div className="relative group">
                                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-0 group-focus-within:opacity-30 transition-opacity blur"></div>
                              <Input 
                                placeholder="@username" 
                                    className="h-14 pl-4 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-800 bg-white dark:bg-zinc-800 rounded-xl transition-all duration-300 relative z-10" 
                                {...field}
                              />
                                  <motion.div 
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 z-10 pointer-events-none"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ 
                                      opacity: field.value ? 1 : 0, 
                                      scale: field.value ? 1 : 0 
                                    }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                  >
                                    {field.value && <CheckCircle2 className="h-5 w-5" />}
                                  </motion.div>
                                </div>
                            </FormControl>
                              <FormMessage className="ml-1" />
                              <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 ml-1">
                                Share your X handle to receive a shoutout for your project.
                              </div>
                          </FormItem>
                        )}
                      />
                      </motion.div>
                    </div>
                  )}
                  
                  {currentStep === 1 && (
                    <EnhancedAnalyzeStep 
                      onAnalysisComplete={handleAnalysisSuccess}
                      onAnalysisStart={handleAnalysisStart}
                      projectId={projectId}
                    />
                  )}
                  
                  {currentStep === 2 && (
                    <MarketDeepDiveQuestions 
                      answers={commitmentAnswers}
                      onAnswersChange={setCommitmentAnswers}
                    />
                  )}
                  
                  {currentStep === 3 && (
                    <AudienceInsightsQuestions 
                      answers={commitmentAnswers}
                      onAnswersChange={setCommitmentAnswers}
                    />
                  )}
                  
                  {currentStep === 4 && (
                    <ContentStrategyQuestions 
                      answers={commitmentAnswers}
                      onAnswersChange={setCommitmentAnswers}
                    />
                  )}
                  
                  {currentStep === 5 && (
                    <div className="py-4">
                      {analysisResults ? (
                        <>
                          <EnhancedGrowthInsights 
                            results={analysisResults} 
                            onInsightsComplete={handleCelebrationComplete}
                            onInsightsStateChange={setInsightsCompleted}
                            onPageChange={setCurrentAnalysisPageTitle}
                          />
                        </>
                      ) : analysisLoading || !analysisComplete ? (
                        // Simple loading state - analysis continues in background
                        <div className="p-8 text-center">
                          <div className="mb-6">
                            <Database className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-pulse" />
                            <h3 className="text-xl font-semibold mb-3">Analysis in Progress</h3>
                            <p className="text-zinc-600 dark:text-zinc-400 mb-6 max-w-md mx-auto">
                              We're still analyzing your website. Your insights will be ready shortly!
                            </p>
                            <div className="inline-flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Usually takes 1-2 minutes</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <div className="mb-6">
                            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-3">Analysis Data Unavailable</h3>
                            <p className="text-zinc-600 dark:text-zinc-400 mb-6 max-w-md mx-auto">
                              We encountered an issue accessing your analysis results. Let's try re-analyzing your website to get fresh insights.
                            </p>
                            <Button 
                              onClick={prevStep}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Go Back to Analysis
                            </Button>
                          </div>
                          
                          {/* Show a default visualization instead of empty space */}
                          <div className="mt-10 p-6 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
                            <h4 className="font-medium text-lg mb-4">Expected Insights Include:</h4>
                            <ul className="space-y-3 text-left">
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-zinc-700 dark:text-zinc-300">Product analysis and key features</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-zinc-700 dark:text-zinc-300">Target audience and customer segments</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-zinc-700 dark:text-zinc-300">Market trends and competitive landscape</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-zinc-700 dark:text-zinc-300">Growth opportunities and strategic recommendations</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </CardContent>
            
            <CardFooter className="flex flex-col gap-4 pt-4 border-t">
              {/* Always show footer navigation for onboarding steps */}
              <div className="flex justify-between w-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className={cn(
                      "border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all duration-200",
                      currentStep === 0 ? "opacity-0 pointer-events-none" : "opacity-100"
                    )}
                  >
                    <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
                    Back
                  </Button>
                
                  {currentStep < steps.length - 1 ? (
                    <Button 
                      type="button" 
                      onClick={nextStep}
                      disabled={
                        (currentStep === 1 && (!analysisComplete || analysisLoading)) || // Disable next if analysis not done or is loading
                        (currentStep === 2 && (!commitmentAnswers.seriousnessScale || !commitmentAnswers.biggestChallenge || !commitmentAnswers.revenueGoal || !commitmentAnswers.timeCommitment)) || // Disable if commitment questions not answered
                        (currentStep === 3 && (!commitmentAnswers.idealCustomer || !commitmentAnswers.mainProblem || !commitmentAnswers.customerHangout || !commitmentAnswers.marketingChannels)) || // Disable if audience questions not answered  
                        (currentStep === 4 && (!commitmentAnswers.brandTopics || !commitmentAnswers.discoveryMethod || !commitmentAnswers.contentFormats || !commitmentAnswers.targetKeywords)) || // Disable if content questions not answered
                        isLoadingCreate
                      }
                      className={cn(
                        "relative group overflow-hidden bg-primary hover:bg-primary/90 transition-all duration-300 px-6 py-2 h-12 text-base",
                        (currentStep === 1 && (!analysisComplete || analysisLoading)) || 
                        (currentStep === 2 && (!commitmentAnswers.seriousnessScale || !commitmentAnswers.biggestChallenge || !commitmentAnswers.revenueGoal || !commitmentAnswers.timeCommitment)) ||
                        (currentStep === 3 && (!commitmentAnswers.idealCustomer || !commitmentAnswers.mainProblem || !commitmentAnswers.customerHangout || !commitmentAnswers.marketingChannels)) ||
                        (currentStep === 4 && (!commitmentAnswers.brandTopics || !commitmentAnswers.discoveryMethod || !commitmentAnswers.contentFormats || !commitmentAnswers.targetKeywords)) ||
                        isLoadingCreate 
                          ? "opacity-70 cursor-not-allowed" 
                          : ""
                      )}
                    >
                      {/* Animated background effect */}
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-blue-400/30 to-blue-400/0"
                        animate={{ 
                          x: ['-100%', '200%'],
                        }}
                        transition={{ 
                          duration: 1.5, 
                          repeat: Infinity,
                          repeatDelay: 0.5,
                          ease: "easeInOut",
                        }}
                        style={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                      />
                      
                      <span className="relative z-10 flex items-center">
                        {currentStep === 1 && analysisLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing...
                          </>
                        ) : (
                          <>
                            {isLoadingCreate ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating...
                              </>
                            ) : (
                              <>
                                Continue
                                <motion.div
                                  animate={{ x: [0, 4, 0] }}
                                  transition={{ duration: 1.5, repeat: Infinity, repeatType: "loop" }}
                                  className="ml-2"
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </motion.div>
                              </>
                            )}
                          </>
                        )}
                      </span>
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={
                        !analysisComplete ||
                        !insightsCompleted ||
                        isLoadingCreate
                      }
                      className={cn(
                        "relative group overflow-hidden bg-primary hover:bg-primary/90 transition-all duration-300 px-6 py-2 h-12 text-base",
                        (!analysisComplete || !insightsCompleted || isLoadingCreate) ? "opacity-70 cursor-not-allowed" : ""
                      )}
                    >
                      {isLoadingCreate ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Finalizing...
                        </>
                      ) : (
                        <>
                          Continue
                          <motion.div
                            animate={{ x: [0, 4, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, repeatType: "loop" }}
                            className="ml-2"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </motion.div>
                        </>
                      )}
                    </Button>
                  )}
                </div>
            </CardFooter>
          </form>
        </Form>
      </Card>
        </>
      )}
    </div>
  );
}